import type {
  AdministrationData,
  AssumptionRecord,
  BookImportRecord,
  BookRecord,
  BookSourceBindingRecord,
  CreateBookInput,
  CaseReplayInput,
  CaseReplayResult,
  CurrentUser,
  DataRightsProfileRecord,
  EvidenceContractDefinition,
  EvidenceContractRecord,
  EvidenceCoverage,
  EvidenceObservationRecord,
  EvidenceTemplate,
  EvidenceSourceBindingRecord,
  EvidenceSourceCatalogRecord,
  EvidenceSourceConfiguration,
  AlertEpisodeRecord,
  AlertEpisodeDetailRecord,
  AssumptionCandidateRecord,
  AssumptionGovernanceProjection,
  ExtractionReviewCommitRecord,
  ThesisInputRecord,
  IdentityResolutionAttempt,
  IntegrationConnectionRecord,
  IntegrationRunRecord,
  ImportMappingRecord,
  ImportReviewData,
  MechanismProjection,
  MechanismVersionDiff,
  OrganizationRecord,
  OrganizationUserRecord,
  ExternalMcpClientRecord,
  ExternalMcpScope,
  CapabilityRolloutDimension,
  CapabilityRolloutFlagRecord,
  PositionRecord,
  QuarantineUploadSession,
  ResearchCaseManifest,
  ResearchCaseSummary,
  ReferenceProfileSummary,
  AuthClientConfig,
  CreateOrganizationInput,
  CreatedOrganization,
  RunProgress,
  ScenarioDraftInput,
  ScenarioRecord,
  ScenarioCatalogRecord,
  ScenarioBudgetProjection,
  ScenarioExposureMatrix,
  ScenarioVersionSummary,
  SftpConnectionConfig,
  ScenarioRunRecord,
  TodayProjection,
  WhatIfRecord,
  WhatIfPromotionRecord,
  WorkspaceData,
  DecisionArtifactRecord,
  InvestigationRecord,
  ReassessmentRecord,
  ExpansionGateProjection,
  ArtifactDeliveryRecord,
  OutboundWebhookEndpointRecord,
  ExtractionRunRecord,
  DashboardProjection,
  DashboardVisit,
  ValuationSummary,
} from "./types.js";
import { replaceProductVocabulary } from "./product-language.js";

interface ApiEnvelope<T> {
  data: T;
}

interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    trace_id?: string;
  };
}

export interface AgentConversationReply {
  schemaVersion: "1.0.0" | "2.0.0";
  reply: string;
  intent: "holdings" | "what_if" | "new_portfolio" | "report" | "exposure" | "clarify";
  answer?: AgentGroundedAnswer;
}
export type AgentConversationIntent = AgentConversationReply["intent"];

export interface AgentStreamStatus {
  readonly phase: string;
  readonly message: string;
  readonly toolName?: string;
  /** One source the answer reads, from the `agent-reply-status.v1` contract (V-A2). */
  readonly step?: AgentProgressStep;
}

export interface AgentProgressStep {
  readonly stepId: string;
  readonly label: string;
  readonly status: "running" | "done" | "failed";
}

export interface AgentStreamTiming {
  readonly stage: string;
  readonly elapsedMs: number;
  readonly transport: string;
}

export interface AgentGroundedAnswer {
  readonly schemaVersion: "1.0.0";
  readonly answerId: string;
  readonly chatId: string;
  readonly runId: string;
  readonly completedAt: string;
  readonly answer: string;
  readonly claims: readonly {
    readonly claimId: string;
    readonly kind: string;
    readonly text: string;
    readonly citationIds: readonly string[];
  }[];
  readonly citations: readonly AgentAnswerCitation[];
  readonly limitations: readonly {
    readonly limitationId: string;
    readonly kind: string;
    readonly message: string;
  }[];
  readonly toolRuns: readonly {
    readonly toolName: string;
    readonly toolCallId: string;
    readonly status: "succeeded" | "partial" | "failed" | "denied" | "cancelled";
  }[];
  readonly draftIntents: readonly AgentDraftIntent[];
  readonly generatedUi?: AgentGeneratedUi;
  readonly provenanceDigest: string;
  readonly answerDigest: string;
}

export interface AgentDraftIntent {
  readonly intentType:
    | "portfolio"
    | "position"
    | "assumption"
    | "scenario"
    | "what_if"
    | "evidence_contract"
    /** Written by the server from the user's own words, never by the model (V-A4). */
    | "trade"
    | "analysis"
    | "report";
  readonly destination: string;
  readonly payload: unknown;
}

export interface AgentGeneratedUi {
  readonly schemaVersion: "1.0.0";
  readonly title?: string;
  readonly components: readonly AgentGeneratedUiComponent[];
}

type AgentDisplayValue = string | number | boolean | null;
export type AgentGeneratedUiComponent =
  | {
      readonly componentId: string;
      readonly kind: "metric";
      readonly label: string;
      readonly value: AgentDisplayValue;
      readonly unit?: string;
      readonly claimIds: readonly string[];
    }
  | {
      readonly componentId: string;
      readonly kind: "table";
      readonly title: string;
      readonly columns: readonly { readonly key: string; readonly label: string }[];
      readonly rows: readonly {
        readonly values: Readonly<Record<string, AgentDisplayValue>>;
        readonly claimIds: readonly string[];
      }[];
    }
  | {
      readonly componentId: string;
      readonly kind: "chart";
      readonly chartType: "line" | "bar";
      readonly title: string;
      readonly xLabel?: string;
      readonly yLabel?: string;
      readonly series: readonly {
        readonly name: string;
        readonly points: readonly {
          readonly x: string | number;
          readonly y: number;
          readonly claimIds: readonly string[];
        }[];
      }[];
    }
  | {
      readonly componentId: string;
      readonly kind: "comparison";
      readonly title: string;
      readonly items: readonly {
        readonly label: string;
        readonly baseline: AgentDisplayValue;
        readonly scenario: AgentDisplayValue;
        readonly delta?: AgentDisplayValue;
        readonly unit?: string;
        readonly claimIds: readonly string[];
      }[];
    }
  | {
      readonly componentId: string;
      readonly kind: "scenario_controls";
      readonly title: string;
      readonly description: string;
      readonly controls: readonly {
        readonly key: string;
        readonly label: string;
        readonly controlType: "number" | "select";
        readonly defaultValue: string | number;
        readonly minimum?: number;
        readonly maximum?: number;
        readonly step?: number;
        readonly options?: readonly string[];
        readonly unit?: string;
      }[];
      readonly draftIntentIndex: number;
    };

export interface AgentAnswerCitation {
  readonly citationId: string;
  readonly referenceType: "dataset" | "document" | "observation" | "analysis" | "source";
  readonly referenceId: string;
  readonly contentDigest?: string;
  readonly title: string;
  readonly provider: string;
  readonly sourceIdentity: string;
  readonly sourceUrl?: string;
  readonly location?:
    | { readonly kind: "rows"; readonly start: number; readonly end: number }
    | { readonly kind: "page"; readonly page: number }
    | { readonly kind: "characters"; readonly start: number; readonly end: number }
    | { readonly kind: "field"; readonly path: string };
  readonly observedAt?: string;
  readonly publishedAt?: string;
  readonly providerAsOf?: string;
  readonly feedClass:
    "historical" | "publication" | "delayed" | "near_real_time" | "real_time" | "unknown";
  readonly delaySeconds?: number;
  readonly retrievedAt: string;
}

export interface AgentConversationTurn {
  role: "user" | "assistant";
  text: string;
}

export interface MarketInstrument {
  readonly symbol: string;
  readonly name: string;
  readonly exchange: string;
  readonly assetClass: "us_equity";
  readonly currency: "USD";
  readonly provider: string;
  /** Told from the name by the server (ADR-099); absent from older responses. */
  readonly type?: "stock" | "etf";
}

export interface MarketQuote {
  readonly symbol: string;
  readonly price: number;
  readonly currency: "USD";
  readonly asOf: string;
  readonly provider: string;
  readonly feed: "iex" | "delayed_sip" | "sip" | "twelve_data" | "cboe_delayed" | "yahoo";
  readonly timeliness:
    | "real_time_single_venue"
    | "delayed_consolidated"
    | "real_time_consolidated"
    | "real_time_provider";
  readonly previousClose?: number;
  readonly previousCloseDate?: string;
}

export type MarketRange = "1D" | "5D" | "1M" | "6M" | "YTD" | "1Y" | "5Y";

/** `market-bars.v1` (V-M3). */
export interface MarketBars {
  readonly symbol: string;
  readonly range: MarketRange;
  readonly interval: "5Min" | "30Min" | "1Day" | "1Week";
  readonly bars: readonly {
    readonly t: string;
    readonly o: number;
    readonly h: number;
    readonly l: number;
    readonly c: number;
    readonly v: number;
  }[];
  readonly provider: string;
  readonly feed: string;
  readonly session?: { readonly open: string; readonly close: string };
  readonly receivedAt: string;
}

/** `watchlist.v1` (V-M2). */
export interface Watchlist {
  readonly items: readonly { readonly symbol: string; readonly addedAt: string }[];
}

export type InstrumentResearchKind =
  "profile" | "metrics" | "financials" | "filings" | "news" | "holdings";

/** `instrument-research.v1` (V-M4): the run fetching one tab, or why no provider was allowed. */
export interface InstrumentResearch {
  readonly symbol: string;
  readonly kind: InstrumentResearchKind;
  readonly commandId: string;
  readonly run?: { readonly id: string; readonly providerKey: string; readonly status: string };
  readonly refusals: readonly { readonly providerKey: string; readonly reason: string }[];
}

/** `instrument-exposure.v1` (V-M5). */
export interface InstrumentExposure {
  readonly symbol: string;
  readonly holdings: readonly {
    readonly bookId: string;
    readonly bookName: string;
    readonly positionId: string;
    readonly quantity?: number;
    readonly marketValue?: number;
    readonly shareOfValuePercent?: number;
    readonly priceAsOf?: string;
    readonly whatIfs: readonly {
      readonly scenarioId: string;
      readonly name: string;
      readonly direction: "up" | "down" | "mixed";
    }[];
  }[];
}

export interface TradingBoundary {
  readonly provider: "alpaca";
  readonly mode: "disabled" | "paper";
  readonly configured: boolean;
  readonly liveTradingAvailable: false;
  readonly autonomousExecutionAvailable: boolean;
  readonly autonomousMode: "disabled" | "paper";
  readonly humanApprovalRequired: boolean;
  readonly autonomyLimits: {
    readonly maximumPositionEquityPercent: number;
    readonly maximumOrderEquityPercent: number;
    readonly maximumOrderNotionalUsd: number;
    readonly maximumPositions: number;
    readonly cashReservePercent: number;
    /** Zero means the daily screen chooses from the whole market. */
    readonly allowlistSize: number;
    readonly screenSizes: readonly UniverseSize[];
    readonly tickSchedule: string;
    /** A goal whose required return rises above this stops as out of reach. */
    readonly maximumRequiredAnnualReturnPercent?: number;
  };
  readonly capitalPolicy: "closed_no_new_deposits";
  readonly allowedAssets: readonly ["long_us_equities"];
  readonly prohibited: readonly string[];
}

export type UniverseSize = 10 | 50 | 100 | 500;

/** The latest pre-open universe screen for a goal (ADR-077). */
export interface UniverseScreenSummary {
  readonly sessionDate: string;
  readonly asOf: string;
  readonly screenSize: UniverseSize;
  readonly feed: "iex" | "sip";
  readonly method: string;
  /** Chosen symbols in rank order. */
  readonly symbols: readonly string[];
  readonly considered: number;
  readonly belowCut: number;
  readonly exclusionCounts: Readonly<Record<string, number>>;
  readonly allowlisted: boolean;
}

export interface AutonomousTick {
  readonly id: string;
  readonly goalId: string;
  readonly status: "running" | "traded" | "skipped" | "halted" | "liquidated" | "failed";
  readonly dispositionReason?: string;
  readonly marketOpen: boolean;
  readonly strategyName: string;
  readonly strategyVersion: string;
  readonly inputsHash: string;
  readonly equity: number;
  readonly cash: number;
  readonly peakEquity: number;
  readonly drawdownPercent: number;
  readonly requiredAnnualReturnPercent?: number;
  readonly universeSize: number;
  /** Candidates the tick did not place, each with a reason. */
  readonly skipped?: readonly {
    readonly symbol?: unknown;
    readonly reason?: unknown;
    /** Set when a firing what-if withheld the entry; older ticks carry the reason only. */
    readonly scenarioBlocks?: readonly {
      readonly scenarioId: string;
      readonly scenarioVersionId: string;
      readonly contractId: string;
    }[];
  }[];
  /** Named scalars recorded with the tick, such as the idle streak or screened symbols. */
  readonly diagnostics?: Readonly<Record<string, number>>;
  /** Each order the planner produced, with whether it was submitted and why not. */
  readonly plannedOrders?: readonly Readonly<Record<string, unknown>>[];
  /** The entry ranking the tick acted on beside the one recorded judgments would give (ADR-105). */
  readonly shadowTilt?: ShadowTilt;
  readonly ordersSubmitted: number;
  readonly providerAsOf: string;
  readonly startedAt: string;
  readonly completedAt?: string;
}

/** `autonomous-shadow-tilt.v1`: nothing in it was acted on. */
export interface ShadowTilt {
  readonly label: "shadow, not acted on";
  readonly status: "computed" | "no_judgments" | "unavailable";
  readonly ruleName: string;
  readonly ruleVersion: string;
  readonly maximumAdjustment: number;
  readonly reason?: string;
  readonly acted: readonly {
    readonly symbol: string;
    readonly rank: number;
    readonly score: number;
  }[];
  readonly tilted: readonly {
    readonly symbol: string;
    readonly rank: number;
    readonly actedRank: number;
    readonly score: number;
    readonly adjustment: number;
    readonly judgment?: {
      readonly judgmentId: string;
      readonly symbol: string;
      readonly direction: "outperform" | "underperform";
      readonly probabilityPercentUncalibrated: number;
      readonly horizonSessions: number;
      readonly asOf: string;
      readonly model: string;
      readonly promptVersion: string;
    };
  }[];
}

export interface InvestmentGoal {
  readonly id: string;
  readonly bookId: string;
  /** The paper account this goal trades (ADR-082). */
  readonly paperAccountId: string;
  readonly status: "draft" | "active" | "paused" | "achieved" | "archived";
  /** What the goal aims for (ADR-087). Only a target goal has a target amount and date. */
  readonly objective: GoalObjective;
  readonly targetAmount?: number;
  readonly targetDate?: string;
  /** A target goal's hurdle rate, for comparison only. */
  readonly hurdleRatePercent?: number;
  /** An income goal's terms (ADR-088). */
  readonly income?: {
    readonly yearlyIncomeTarget: number;
    readonly distributionPolicy: "cash" | "reinvest";
    readonly yieldFloorPercent: 2 | 3 | 4;
  };
  readonly benchmark?: {
    readonly benchmark: GoalBenchmark;
    readonly excessReturnTargetPercent: number;
    readonly measurementPeriod: MeasurementPeriod;
  };
  readonly maxDrawdownPercent: number;
  readonly baselineEquity?: number;
  readonly baselineCash?: number;
  readonly baselineAt?: string;
  readonly externalContributionsLocked: true;
  readonly autonomyMode: "manual" | "autonomous";
  readonly autonomyEnabledAt?: string;
  readonly screenSize: UniverseSize;
  readonly peakEquity?: number;
  readonly lastTickAt?: string;
  /** Set while an operator's stop is settling. The goal keeps ticking, only to liquidate. */
  readonly stopRequestedAt?: string;
  readonly pauseReason?: string;
  /** Why the goal paused, as a code. Absent for goals paused before codes were recorded. */
  readonly pauseCode?: GoalPauseCode;
  /** The goal's own trading policy (ADR-083); fixed while autonomy is on. */
  readonly policy: GoalTradingPolicy;
  /** How often the goal checks (ADR-084); absent means the deployment's own settings. */
  readonly cadence?: TickCadence;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A paper account an organization trades through (ADR-082). It never carries a key. */
export interface PaperAccount {
  readonly id: string;
  readonly name: string;
  readonly kind: "secret_manager" | "deployment" | "local";
  /** Which broker the account is with (ADR-101). */
  readonly provider: "alpaca" | "tradier";
  /** Typed keys, approved on Alpaca (ADR-100), or a Tradier sandbox token (ADR-101). */
  readonly credentialType: "api_keys" | "alpaca_oauth" | "tradier_sandbox_token";
  readonly status: "active" | "retired";
  /** The last four characters of the Alpaca account id, once known. */
  readonly displaySuffix?: string;
  /** The live goal holding the account; at most one. */
  readonly liveGoalId?: string;
  readonly revision: number;
  readonly createdAt: string;
  readonly retiredAt?: string;
}

export interface PaperAccountKeys {
  readonly keyId: string;
  readonly secretKey: string;
}

/** Each bundle fixes how often a goal checks, its bar interval and its freshness limit together. */
export type TickCadence = "every_15_minutes" | "hourly" | "daily";

export type TradingSector =
  | "technology"
  | "health_care"
  | "financials"
  | "consumer_discretionary"
  | "consumer_staples"
  | "energy"
  | "industrials"
  | "materials"
  | "utilities"
  | "real_estate"
  | "communication";

/** Every limit is optional: absent means the deployment's value, or off. Limits only tighten. */
export interface GoalTradingPolicy {
  readonly limits: {
    readonly maximumPositionEquityPercent?: number;
    readonly maximumPositions?: number;
    readonly maximumOrderNotionalUsd?: number;
    readonly cashReservePercent?: number;
    readonly maximumSectorEquityPercent?: number;
    readonly maximumBuysPerFiveSessions?: number;
    readonly volatilityTargetPercent?: number;
  };
  readonly scenarioRiskFilter: boolean;
  readonly restrictedSymbols: readonly string[];
  readonly excludedSectors: readonly TradingSector[];
  readonly preferredSectors: readonly TradingSector[];
  /** Written guidance these rules came from, read once at setup (ADR-086). */
  readonly guidance?: GoalGuidance;
}

export type GuidanceRuleKind = "restricted_symbol" | "excluded_sector" | "preferred_sector";

export interface GoalGuidance {
  readonly text: string;
  readonly runId: string;
  readonly applied: readonly { readonly kind: GuidanceRuleKind; readonly value: string }[];
}

/** One read of written guidance into rules (ADR-086). */
export interface GoalGuidanceRun {
  readonly id: string;
  readonly status: "completed" | "failed";
  readonly text: string;
  readonly rules: readonly {
    readonly kind: GuidanceRuleKind;
    readonly value: string;
    readonly source_quote: string;
  }[];
  readonly notApplied: readonly { readonly source_quote: string; readonly reason: string }[];
  readonly failure?: string;
  readonly createdAt: string;
}

export type GoalPauseCode =
  | "external_contribution"
  | "account_ineligible"
  | "account_changed"
  | "horizon_expired"
  | "drawdown_breached"
  | "unreachable"
  | "operator_stop";

/** One page of a list, newest first, and the cursor for the next page when there is one. */
export interface Page<T> {
  readonly data: readonly T[];
  readonly page: { readonly next: string | null };
}

export interface TickListFilter {
  readonly limit?: number;
  /** ISO time; keeps ticks started at or after it. */
  readonly since?: string;
  /** Keeps only ticks that traded or closed positions. */
  readonly traded?: boolean;
  readonly before?: string;
}

export interface ProposalListFilter {
  readonly limit?: number;
  readonly status?: "open" | "filled" | "all";
  readonly origin?: "human" | "autonomous";
  readonly side?: "buy" | "sell";
  readonly before?: string;
}

/**
 * A trade a goal opened and closed, recorded by the worker once the closing session has a close to
 * judge it by (O3). Mirrors `trade-outcome.v1` in the contracts.
 */
export interface TradeOutcome {
  readonly id: string;
  readonly goalId: string;
  readonly symbol: string;
  readonly openedAt: string;
  readonly closedAt: string;
  readonly quantityBought: number;
  readonly averageEntryPrice: number;
  readonly averageExitPrice: number;
  readonly costBasis: number;
  readonly proceeds: number;
  readonly realizedProfit: number;
  readonly returnPercent: number;
  readonly heldDays: number;
  readonly entryProposalIds: readonly string[];
  readonly exitProposalIds: readonly string[];
  readonly origin: "autonomous" | "by_hand" | "mixed";
  readonly closedBy: "strategy_exit" | "liquidation" | "by_hand";
  readonly openingTickId?: string;
  readonly closingTickId?: string;
  readonly strategy?: { readonly name: string; readonly version: string };
  readonly cadence?: TickCadence;
  readonly entrySlippageBps?: number;
  readonly exitSlippageBps?: number;
  readonly benchmark?: { readonly symbol: "SPY"; readonly returnPercent: number };
  readonly recordedAt: string;
}

/** What an operator did to a goal, beside what the ticks decided. */
export interface InvestmentGoalEvent {
  readonly id: string;
  readonly goalId: string;
  readonly action:
    | "terms_changed"
    | "stop_requested"
    | "stop_completed"
    | "archived"
    | "autonomy_enabled"
    | "autonomy_disabled"
    | "policy_changed";
  readonly detail: Record<string, unknown>;
  readonly actorId: string;
  readonly createdAt: string;
}

export interface BrokerageGoalSnapshot {
  readonly id: string;
  readonly goalId: string;
  readonly provider: "alpaca";
  readonly environment: "paper";
  readonly accountDisplaySuffix: string;
  readonly accountStatus: string;
  readonly equity: number;
  readonly cash: number;
  readonly buyingPower: number;
  readonly lastEquity: number;
  readonly multiplier: string;
  readonly tradingBlocked: boolean;
  readonly accountBlocked: boolean;
  readonly patternDayTrader: boolean;
  readonly positions: readonly {
    readonly symbol: string;
    readonly quantity: number;
    readonly averageEntryPrice: number;
    readonly marketValue: number;
    readonly currentPrice: number;
    readonly unrealizedProfitLoss: number;
  }[];
  readonly externalContributionsAfterBaseline: number;
  readonly sourceAsOf: string;
  readonly createdAt: string;
}

/** `trade-preview.v1`: the goal's limits applied to one order (V-A4). */
export interface TradePreview {
  readonly goalId: string;
  readonly mode: "running" | "manual" | "inactive";
  readonly checks: readonly {
    readonly key: "what_if" | "largest_position" | "per_order" | "cash_reserve";
    readonly label: string;
    readonly status: "pass" | "fail" | "not_confirmed";
    readonly detail: string;
  }[];
  readonly problems: readonly string[];
  readonly passed: boolean;
  readonly checkedAt: string;
}

export interface TradeProposal {
  readonly id: string;
  readonly goalId: string;
  readonly bookId: string;
  readonly symbol: string;
  readonly side: "buy" | "sell";
  /** Who placed it: a person, or the goal's autonomous trader (ADR-070). */
  readonly origin: "human" | "autonomous";
  readonly notional?: number;
  readonly quantity?: number;
  readonly rationale: string;
  readonly sourceRefs: readonly string[];
  readonly status:
    "draft" | "approved" | "submitting" | "submitted" | "rejected" | "failed" | "cancelled";
  readonly riskChecks: {
    readonly checkedAt?: string;
    readonly rules?: readonly string[];
    readonly maximumAllowedNotional?: number;
    readonly availableCash?: number;
    readonly ownedQuantity?: number;
  };
  readonly providerOrderStatus?: string;
  readonly filledQuantity?: number;
  readonly filledAveragePrice?: number;
  readonly filledAt?: string;
  readonly providerStatusCheckedAt?: string;
  readonly rejectionReason?: string;
  readonly revision: number;
  readonly approvedAt?: string;
  readonly submittedAt?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export type GoalObjective = "target" | "benchmark" | "income";
export type GoalBenchmark = "sp500" | "sixty_forty" | "cash";
export type MeasurementPeriod = "rolling_12_months" | "since_start";

/** A benchmark goal's lead or lag, measured from recorded prices (ADR-087). */
export type BenchmarkMeasurement = {
  readonly benchmark: GoalBenchmark;
  readonly measurementPeriod: MeasurementPeriod;
  readonly excessReturnTargetPercent: number;
  readonly disclaimer: string;
} & (
  | {
      readonly state: "measured";
      readonly from: string;
      readonly to: string;
      readonly years: number;
      readonly goalReturnPercent: number;
      readonly benchmarkReturnPercent: number;
      readonly toBeatPercent: number;
      readonly marginPercent: number;
      readonly ahead: boolean;
    }
  | { readonly state: "insufficient_data"; readonly reason: string }
);

export type GoalFeasibility =
  | {
      readonly state: "no_target";
      readonly currentCapital?: number;
      readonly baselineCapital?: number;
      readonly disclaimer: string;
    }
  | {
      readonly state: "awaiting_baseline";
      readonly targetAmount: number;
      readonly targetDate: string;
      readonly disclaimer: string;
    }
  | {
      readonly state: "calculated" | "overdue";
      readonly currentCapital: number;
      readonly baselineCapital: number;
      readonly targetAmount: number;
      readonly targetDate: string;
      readonly targetMultiple: number;
      readonly yearsRemaining: number;
      readonly requiredAnnualReturnPercent: number | null;
      readonly progressPercent: number;
      readonly disclaimer: string;
    };

export interface GoalTradingProjection {
  readonly goal: InvestmentGoal | null;
  readonly snapshot: BrokerageGoalSnapshot | null;
  readonly proposals: readonly TradeProposal[];
  readonly universeScreen?: UniverseScreenSummary | null;
  readonly feasibility?: GoalFeasibility;
  readonly benchmarkMeasurement?: BenchmarkMeasurement | null;
  /** An income goal's dividends received, as recorded (ADR-088). */
  readonly incomeMeasurement?: {
    readonly yearlyIncomeTarget: number;
    readonly distributionPolicy: "cash" | "reinvest";
    readonly yieldFloorPercent: number;
    readonly receivedLastTwelveMonths: number;
    readonly receivedSinceStart: number;
    readonly yieldNeededPercent?: number;
    readonly disclaimer: string;
  } | null;
  /** When an autonomous goal is next scheduled to check; the check may still skip. */
  readonly nextCheckAt?: string | null;
  /** The goal's paper account is retired: it cannot sell or sync, so the goal can only be filed. */
  readonly paperAccountRetired?: boolean;
  readonly trading: TradingBoundary;
}

/** The calibration report (ADR-089). */
export interface CalibrationReport {
  readonly label: string;
  readonly question: string;
  readonly minimumSample: number;
  readonly counts: {
    readonly judgments: number;
    readonly scored: number;
    readonly notObserved: number;
    readonly awaitingOutcome: number;
    readonly awaitingShockResolution: number;
  };
  readonly groups: readonly {
    readonly model: string;
    readonly promptVersion: string;
    readonly kind: "unconditional" | "conditional";
    readonly scored: number;
    readonly hitRatePercent?: number;
    readonly brierScore?: number;
    readonly reliability?: readonly {
      readonly band: string;
      readonly count: number;
      readonly meanProbabilityPercent: number;
      readonly observedPercent: number;
    }[];
  }[];
}

/** One of the caller's Ask Polyfuture conversations, from the server (ADR-090). */
export interface AgentChatSummary {
  readonly chatId: string;
  readonly title: string;
  readonly questions: number;
  readonly startedAt: string;
  readonly lastActivityAt: string;
}

export interface AgentChatMessage {
  readonly messageId: string;
  readonly runId: string;
  readonly question: string;
  readonly askedAt: string;
  readonly bookId?: string;
  readonly status: "answered" | "failed" | "pending";
  readonly answer?: AgentGroundedAnswer;
  readonly attachments?: readonly { readonly id: string; readonly fileName: string }[];
}

export interface AgentChat {
  readonly chatId: string;
  readonly messages: readonly AgentChatMessage[];
}

/** A saved research result, copied by the server from a stored answer (ADR-091). */
export interface ResearchResult {
  readonly id: string;
  readonly bookId: string;
  readonly bookName: string;
  readonly answerId: string;
  readonly chatId: string;
  readonly title: string;
  readonly question: string;
  readonly answer: string;
  readonly facts: readonly {
    readonly claimId: string;
    readonly kind: string;
    readonly text: string;
    readonly citationIds: readonly string[];
  }[];
  readonly figures: readonly AgentGeneratedUiComponent[];
  readonly sources: readonly AgentAnswerCitation[];
  readonly limitations: readonly {
    readonly limitationId: string;
    readonly kind: string;
    readonly message: string;
  }[];
  readonly answerDigest: string;
  readonly answeredAt: string;
  readonly savedBy: string;
  readonly savedAt: string;
}

export type ResearchResultSummary = Pick<
  ResearchResult,
  | "id"
  | "bookId"
  | "bookName"
  | "answerId"
  | "title"
  | "question"
  | "answeredAt"
  | "savedBy"
  | "savedAt"
> & { readonly facts: number; readonly sources: number };

/** ADR-092. A file attached to an Ask Polyfuture question, as the server reports it. */
export interface ChatAttachment {
  readonly id: string;
  readonly fileName: string;
  readonly mediaType: string;
  readonly sizeBytes: number;
  readonly contentSha256: string;
  readonly status: "reserved" | "uploaded" | "processing" | "ready" | "rejected" | "failed";
  readonly detectedType?: string;
  readonly kind?: "text" | "image";
  readonly truncated?: boolean;
  readonly notes: readonly string[];
  readonly reason?: string;
  readonly createdAt: string;
  readonly processedAt?: string;
}

interface ChatAttachmentUploadSession {
  readonly attachmentId: string;
  readonly uploadMethod: "POST" | "PUT";
  readonly uploadUrl: string;
  readonly formFields: Readonly<Record<string, string>>;
  readonly expiresAt: string;
}

/** V-S1d. A page under More across every portfolio. */
export type PortfolioListKind = "research_notes" | "reports" | "analysis" | "reviews" | "episodes";

export interface PortfolioListRow {
  readonly kind: PortfolioListKind;
  readonly target: "investigation" | "report" | "analysis_run" | "portfolio_reviews" | "episode";
  readonly id: string;
  readonly bookId: string;
  readonly bookName: string;
  readonly title: string;
  readonly status: string;
  readonly at: string;
}

/** One card in the trading goals list (v3 `02-trader.md` §2.1). */
export interface GoalSummary {
  readonly goal: InvestmentGoal;
  readonly bookName: string;
  readonly paperAccountName: string;
  readonly paperAccountSuffix?: string;
  readonly latestSnapshot?: { readonly equity: number; readonly sourceAsOf: string };
  readonly lastTick?: {
    readonly status: AutonomousTick["status"];
    readonly dispositionReason?: string;
    readonly equity: number;
    readonly startedAt: string;
  };
  readonly feasibility: GoalFeasibility;
  readonly nextCheckAt: string | null;
}

export interface GoalList {
  readonly goals: readonly GoalSummary[];
  readonly trading: TradingBoundary;
}

export interface DataCatalogPage {
  readonly catalogDigest: string;
  readonly commands: readonly DataCatalogCommand[];
  readonly nextOffset?: number;
}

export interface DataCatalogCommand {
  readonly commandId: string;
  readonly version: string;
  readonly manifestDigest: string;
  readonly capabilityFamily: string;
  readonly category: string;
  readonly providers: readonly string[];
  readonly interactive: boolean;
  readonly monitorable: boolean;
  readonly agentVisible: boolean;
  readonly name: string;
  readonly description: string;
  readonly resultShape: string;
}

export interface GovernedScenarioTemplate {
  readonly key: string;
  readonly version: string;
  readonly title: string;
  readonly description: string;
  readonly shockClass: string;
  readonly requiredCommandIds: readonly string[];
  readonly parameters: readonly {
    readonly key: string;
    readonly label: string;
    readonly unit: string;
    readonly defaultValue: number;
    readonly minimum: number;
    readonly maximum: number;
    readonly step: number;
  }[];
  readonly methodId: string;
  readonly limitations: readonly string[];
}

export interface OpenbbCapabilityPack {
  readonly key: string;
  readonly title: string;
  readonly families: readonly string[];
  readonly description: string;
  readonly productSurfaces: readonly string[];
  readonly featuredCommandIds: readonly string[];
  readonly scenarioTemplates: readonly GovernedScenarioTemplate[];
}

export interface OpenbbCapabilityPackCatalog {
  readonly schemaVersion: "1.0.0";
  readonly packs: readonly OpenbbCapabilityPack[];
}

export interface DataCommandManifest {
  readonly commandId: string;
  readonly version: string;
  readonly name: string;
  readonly description: string;
  readonly capabilityFamily: string;
  readonly category: string;
  readonly parameterSchema: {
    readonly type?: string;
    readonly properties?: Readonly<Record<string, DataParameterSchema>>;
    readonly required?: readonly string[];
  };
  readonly resultSchema: Readonly<Record<string, unknown>>;
  readonly providers: readonly {
    readonly key: string;
    readonly displayName: string;
    readonly requiresConnection: boolean;
    readonly entitlementClass: string;
  }[];
  readonly resultShape: string;
  readonly interaction: {
    readonly interactive: boolean;
    readonly monitorable: boolean;
  };
  readonly classifications?: {
    readonly interactive: boolean;
    readonly schedulable: boolean;
    readonly monitorable: boolean;
    readonly historicalOnly: boolean;
    readonly agentVisible: boolean;
  };
  readonly allowedPurposes?: readonly string[];
  readonly semantics?: {
    readonly observationTimeField?: string;
    readonly publicationTimeField?: string;
    readonly correctionBehavior?:
      "append_revision" | "replace_snapshot" | "provider_defined" | "not_applicable" | "unknown";
  };
  readonly manifestDigest: string;
}

export interface DataParameterSchema {
  readonly type?: "string" | "number" | "integer" | "boolean" | "array" | "object";
  readonly title?: string;
  readonly description?: string;
  readonly enum?: readonly (string | number)[];
  readonly default?: unknown;
  readonly format?: string;
  readonly minimum?: number;
  readonly maximum?: number;
}

export interface DataQueryRun {
  readonly id: string;
  readonly purpose: string;
  readonly commandId: string;
  readonly commandVersion: string;
  readonly catalogDigest: string;
  readonly providerKey: string;
  readonly status:
    | "queued"
    | "authorized"
    | "running"
    | "validating"
    | "succeeded"
    | "failed"
    | "cancelled"
    | "rights_blocked";
  readonly outputMode: string;
  readonly budgets: DataProviderBudget;
  readonly resultRows: number;
  readonly resultBytes: number;
  readonly executionAttempt: number;
  readonly datasetId?: string;
  readonly error?: { readonly code: string; readonly message: string };
  readonly completedAt?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DatasetPreview {
  readonly datasetId: string;
  readonly rows: readonly Record<string, unknown>[];
  readonly returnedRows: number;
  readonly totalRows: number;
  readonly truncated: boolean;
}

export interface AnalysisMethod {
  readonly schemaVersion: "1.0.0";
  readonly methodId: string;
  readonly version: string;
  readonly name: string;
  readonly description: string;
  readonly capabilityFamily: "J" | "K";
  readonly parameterSchema: {
    readonly type?: string;
    readonly properties?: Readonly<Record<string, DataParameterSchema>>;
    readonly required?: readonly string[];
  };
  readonly implementationDigest: string;
  readonly containerDigest: string;
  readonly deterministic: boolean;
  readonly requiredHistory: {
    readonly minimumObservations: number;
    readonly requiresPointInTimeVintage: boolean;
  };
  readonly limitations: readonly string[];
  readonly manifestDigest: string;
}

export interface AnalysisWorkspace {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly status: "active" | "archived";
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AnalysisArtifact {
  readonly id: string;
  readonly analysisRunId: string;
  readonly artifactType: "dataset" | "table" | "series" | "chart_spec" | "model" | "report";
  readonly contentDigest: string;
  readonly inlineContent?: unknown;
  readonly storageRef?: string;
  readonly byteSize: number;
  readonly validation: unknown;
  readonly provenance: unknown;
  readonly createdAt: string;
}

export interface AnalysisRun {
  readonly id: string;
  readonly workspaceId: string;
  readonly purpose: string;
  readonly methodId: string;
  readonly methodVersion: string;
  readonly methodDigest: string;
  readonly parameters: unknown;
  readonly status:
    | "queued"
    | "authorized"
    | "running"
    | "validating"
    | "succeeded"
    | "failed"
    | "cancelled"
    | "rights_blocked";
  readonly implementationDigest: string;
  readonly containerDigest: string;
  readonly inputDatasetIds: readonly string[];
  readonly artifacts: readonly AnalysisArtifact[];
  readonly diagnostics?: unknown;
  readonly warnings: readonly unknown[];
  readonly limitations: readonly string[];
  readonly error?: { readonly code: string; readonly message: string };
  readonly completedAt?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface GovernedDocumentManifest {
  readonly schemaVersion: "1.0.0";
  readonly documentId: string;
  readonly datasetId: string;
  readonly ordinal: number;
  readonly commandId: string;
  readonly providerKey: string;
  readonly title: string;
  readonly sourceIdentity: string;
  readonly sourceUrl?: string;
  readonly publishedAt?: string;
  readonly receivedAt: string;
  readonly contentDigest: string;
  readonly contentCharacters: number;
  readonly entityKeys: readonly string[];
}

export interface GovernedDocumentSearchHit {
  readonly schemaVersion: "1.0.0";
  readonly document: GovernedDocumentManifest;
  readonly location: {
    readonly kind: "characters";
    readonly start: number;
    readonly end: number;
    readonly chunkIndex: number;
  };
  readonly excerpt: string;
  readonly rank: number;
  readonly contentDigest: string;
  readonly untrustedContent: true;
}

export interface GovernedDocumentSearchResult {
  readonly schemaVersion: "1.0.0";
  readonly query: string;
  readonly hits: readonly GovernedDocumentSearchHit[];
  readonly bounded: true;
  readonly limitations: readonly string[];
}

export interface DocumentEventCandidate {
  readonly schemaVersion: "1.0.0";
  readonly candidateId: string;
  readonly documentId: string;
  readonly eventType: string;
  readonly summary: string;
  readonly status: "pending_review" | "accepted" | "rejected";
  readonly location: { readonly kind: "characters"; readonly start: number; readonly end: number };
  readonly createdAt: string;
}

export interface MonitorSourceBinding {
  readonly id: string;
  readonly bookId: string;
  readonly contractId: string;
  readonly sourceKind: "command" | "legacy_adapter";
  readonly commandId?: string;
  readonly commandVersion?: string;
  readonly providerKey?: string;
  readonly connectionId?: string;
  readonly parameters: Readonly<Record<string, unknown>>;
  readonly selector: Readonly<Record<string, unknown>>;
  readonly metricKey: string;
  readonly unit: string;
  readonly correctionBehavior: "append_revision" | "replace_snapshot" | "provider_defined";
  readonly materiality: {
    readonly minimumAbsoluteChange: number;
    readonly minimumRelativeChangePct: number;
  };
  readonly pollCadenceMinutes: number;
  readonly freshnessSlaMinutes: number;
  readonly enabled: boolean;
  readonly health: "unavailable" | "fresh" | "due" | "stale" | "failed";
  readonly healthReason: string;
  readonly lastPollAt?: string;
  readonly lastSuccessAt?: string;
  readonly nextPollAt?: string;
  readonly lastValue?: number;
  readonly revision: number;
}

export interface DataProviderCatalogRecord {
  readonly key: string;
  readonly displayName: string;
  readonly packages: readonly { readonly name: string; readonly version: string }[];
  readonly entitlementClasses: readonly string[];
  readonly requiresConnection: boolean;
  readonly commandCount: number;
  readonly configuredConnections: number;
}

export interface DataProviderBudget {
  readonly maxRows: number;
  readonly maxBytes: number;
  readonly timeoutSeconds: number;
  readonly maxRetries: number;
  readonly maxProviderCostMicros: number;
}

export interface DataProviderConnectionConfig {
  readonly schemaVersion: "1.0.0";
  readonly providerKey: string;
  readonly commandAllowlist: readonly string[];
  readonly testQuery: {
    readonly commandId: string;
    readonly parameters: Readonly<Record<string, unknown>>;
  };
}

export interface DataProviderConnectionRecord {
  readonly id: string;
  readonly name: string;
  readonly providerKey: string;
  readonly entitlementMode: "customer_supplied" | "customer_entitled";
  readonly credentialConfigured: boolean;
  readonly credentialVersion?: string;
  readonly rightsProfileId: string;
  readonly providerQuota: DataProviderBudget;
  readonly status: "draft" | "enabled" | "suspended" | "retired";
  readonly health:
    "healthy" | "degraded" | "stale" | "auth_failed" | "schema_changed" | "rights_blocked";
  readonly healthReason?: string;
  readonly lastSuccessAt?: string;
  readonly revision: number;
  readonly config: DataProviderConnectionConfig;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DataProviderConnectionOption {
  readonly id: string;
  readonly name: string;
  readonly providerKey: string;
  readonly health: DataProviderConnectionRecord["health"];
  readonly lastSuccessAt?: string;
}

export class ApiRequestError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code = "request_failed",
    public readonly traceId?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

type Fetcher = typeof fetch;

/** Whether the last request reached the server, and when. Any HTTP answer counts as reached. */
export interface ConnectionReport {
  reachable: boolean;
  at: number;
}

export class PolyfutureApiClient {
  private csrfTokenRequired = true;
  private readonly unauthorizedListeners = new Set<() => void>();
  private readonly connectionListeners = new Set<(report: ConnectionReport) => void>();
  // Coalesce concurrent reads only. Never persist session or risk data in a response cache.
  private readonly pendingReads = new Map<string, Promise<unknown>>();

  public constructor(
    private readonly fetcher: Fetcher = (input, init) => fetch(input, init),
    private readonly eventSourceFactory: (url: string) => EventSource = (url) =>
      new EventSource(url, { withCredentials: true }),
    private readonly cookieReader: () => string = () => document.cookie,
  ) {}

  public async getCurrentUser(): Promise<CurrentUser> {
    const user = await this.get<CurrentUser>("/v1/me");
    this.csrfTokenRequired = user.csrfTokenRequired !== false;
    return user;
  }

  public onUnauthorized(listener: () => void): () => void {
    this.unauthorizedListeners.add(listener);
    return () => this.unauthorizedListeners.delete(listener);
  }

  public onConnection(listener: (report: ConnectionReport) => void): () => void {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  /** Asks the server for the session only to learn whether it can be reached. */
  public async probeConnection(): Promise<void> {
    try {
      await this.request<unknown>("/v1/me", { method: "GET" });
    } catch {
      // The connection report has already been published; the probe has no other result.
    }
  }

  public listBooks(): Promise<BookRecord[]> {
    return this.get<BookRecord[]>("/v1/books");
  }

  public searchMarketInstruments(query: string): Promise<MarketInstrument[]> {
    return this.get<MarketInstrument[]>(`/v1/market/instruments?q=${encodeURIComponent(query)}`);
  }

  public getMarketQuotes(symbols: readonly string[]): Promise<MarketQuote[]> {
    return this.get<MarketQuote[]>(
      `/v1/market/quotes?symbols=${encodeURIComponent(symbols.join(","))}`,
    );
  }

  public listBookPositions(bookId: string): Promise<PositionRecord[]> {
    return this.get<PositionRecord[]>(`/v1/books/${encodeURIComponent(bookId)}/positions`);
  }

  public getMarketBars(symbols: readonly string[], range: MarketRange): Promise<MarketBars[]> {
    return this.get<MarketBars[]>(
      `/v1/market/bars?symbols=${encodeURIComponent(symbols.join(","))}&range=${range}`,
    );
  }

  public getWatchlist(): Promise<Watchlist> {
    return this.get<Watchlist>("/v1/market/watchlist");
  }

  public addToWatchlist(symbol: string): Promise<Watchlist> {
    return this.mutation<Watchlist>(
      "PUT",
      `/v1/market/watchlist/${encodeURIComponent(symbol)}`,
      undefined,
    );
  }

  public removeFromWatchlist(symbol: string): Promise<Watchlist> {
    return this.mutation<Watchlist>(
      "DELETE",
      `/v1/market/watchlist/${encodeURIComponent(symbol)}`,
      undefined,
    );
  }

  public requestInstrumentResearch(
    symbol: string,
    kind: InstrumentResearchKind,
    attempt = 0,
  ): Promise<InstrumentResearch> {
    return this.command<InstrumentResearch>(
      `/v1/market/instruments/${encodeURIComponent(symbol)}/research`,
      { kind, attempt },
    );
  }

  public getInstrumentExposure(symbol: string): Promise<InstrumentExposure> {
    return this.get<InstrumentExposure>(
      `/v1/market/instruments/${encodeURIComponent(symbol)}/exposure`,
    );
  }

  public refreshMarketValuations(bookId: string): Promise<{
    bookId: string;
    eligiblePositionCount: number;
    quotedPositionCount: number;
    missingQuoteSymbols: string[];
    totalMarketValue: number;
    currency: "USD";
    asOf?: string;
  }> {
    return this.command(`/v1/books/${bookId}/market-valuations:refresh`, {});
  }

  public listInvestmentGoals(): Promise<GoalList> {
    return this.get<GoalList>("/v1/investment-goals");
  }

  public getInvestmentGoal(goalId: string): Promise<GoalTradingProjection> {
    return this.get<GoalTradingProjection>(`/v1/investment-goals/${encodeURIComponent(goalId)}`);
  }

  public getGoalTradingProjection(bookId: string): Promise<GoalTradingProjection> {
    return this.get<GoalTradingProjection>(
      `/v1/books/${encodeURIComponent(bookId)}/investment-goal`,
    );
  }

  public createInvestmentGoal(
    bookId: string,
    input: {
      readonly targetAmount: number;
      readonly targetDate: string;
      readonly maxDrawdownPercent: number;
    },
  ): Promise<InvestmentGoal> {
    return this.command<InvestmentGoal>(
      `/v1/books/${encodeURIComponent(bookId)}/investment-goal`,
      input,
    );
  }

  /**
   * ADR-085. Admin-only: creates the goal, fixes its baseline and turns autonomy on, or changes
   * nothing. Pass the same `idempotencyKey` when retrying one attempt, so a lost response cannot
   * start a second goal.
   */
  public startAutonomousGoal(
    bookId: string,
    input: (
      | {
          readonly objective?: "target";
          readonly targetAmount: number;
          readonly targetDate: string;
          readonly hurdleRatePercent?: number;
        }
      | {
          readonly objective: "benchmark";
          readonly benchmark: GoalBenchmark;
          readonly excessReturnTargetPercent: number;
          readonly measurementPeriod: MeasurementPeriod;
        }
      | {
          readonly objective: "income";
          readonly yearlyIncomeTarget: number;
          readonly distributionPolicy: "cash" | "reinvest";
          readonly yieldFloorPercent: 2 | 3 | 4;
        }
    ) & {
      readonly maxDrawdownPercent: number;
      readonly paperAccountId?: string;
      readonly policy?: GoalTradingPolicy;
      readonly screenSize: UniverseSize;
      readonly cadence: TickCadence;
    },
    idempotencyKey: string,
  ): Promise<GoalTradingProjection> {
    return this.command<GoalTradingProjection>(
      `/v1/books/${encodeURIComponent(bookId)}/autonomous-goal`,
      input,
      { "Idempotency-Key": idempotencyKey },
    );
  }

  /** ADR-086. Admin-only; reuse the key when retrying one read so it is not read twice. */
  public readGoalGuidance(text: string, idempotencyKey: string): Promise<GoalGuidanceRun> {
    return this.command<GoalGuidanceRun>(
      "/v1/goal-guidance-runs",
      { text },
      { "Idempotency-Key": idempotencyKey },
    );
  }

  /** ADR-090. The caller's own conversations, most recently active first. */
  public listAgentChats(): Promise<readonly AgentChatSummary[]> {
    return this.get<readonly AgentChatSummary[]>("/v1/agent/chats");
  }

  /** ADR-090. One of the caller's conversations with its stored answers. */
  public getAgentChat(chatId: string): Promise<AgentChat> {
    return this.get<AgentChat>(`/v1/agent/chats/${encodeURIComponent(chatId)}`);
  }

  /** ADR-091. The server copies the stored answer; the browser sends only these three fields. */
  public saveResearchResult(input: {
    readonly answerId: string;
    readonly bookId: string;
    readonly title: string;
  }): Promise<ResearchResult> {
    return this.command<ResearchResult>("/v1/research-results", input);
  }

  public listResearchResults(bookId?: string): Promise<readonly ResearchResultSummary[]> {
    return this.get<readonly ResearchResultSummary[]>(
      bookId === undefined
        ? "/v1/research-results"
        : `/v1/research-results?bookId=${encodeURIComponent(bookId)}`,
    );
  }

  public getResearchResult(id: string): Promise<ResearchResult> {
    return this.get<ResearchResult>(`/v1/research-results/${encodeURIComponent(id)}`);
  }

  /** V-S1d. Items for a page under More from every portfolio, each naming its portfolio. */
  public listAcrossPortfolios(kind: PortfolioListKind): Promise<readonly PortfolioListRow[]> {
    return this.get<readonly PortfolioListRow[]>(`/v1/portfolio-lists/${kind}`);
  }

  /** ADR-089. Judgments scored against recorded outcomes; every figure is uncalibrated. */
  public getCalibrationReport(): Promise<CalibrationReport> {
    return this.get<CalibrationReport>("/v1/calibration/report");
  }

  /** ADR-089. Whether a what-if's shock happened; recorded once. */
  public recordWhatIfOutcome(
    whatIfRunId: string,
    input: { readonly occurred: boolean; readonly occurredOn?: string; readonly notes: string },
  ): Promise<unknown> {
    return this.command(`/v1/what-if-runs/${encodeURIComponent(whatIfRunId)}/outcome`, input);
  }

  /** `requirements.md` §4.5. How a scenario resolved; recorded once, then no engine revision. */
  public recordScenarioOutcome(
    scenarioId: string,
    input: {
      readonly materialized: boolean;
      readonly materializedOn?: string;
      readonly notes: string;
    },
  ): Promise<unknown> {
    return this.command(`/v1/scenarios/${encodeURIComponent(scenarioId)}/outcome`, input);
  }

  public syncInvestmentGoal(goalId: string): Promise<GoalTradingProjection> {
    return this.command<GoalTradingProjection>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/sync`,
      {},
    );
  }

  public createTradeProposal(
    goalId: string,
    input:
      | {
          readonly side: "buy";
          readonly symbol: string;
          readonly notional: number;
          readonly rationale: string;
          readonly sourceRefs: readonly string[];
          /** The Ask answer whose trade draft this was saved from (V-A4). */
          readonly answerId?: string;
        }
      | {
          readonly side: "sell";
          readonly symbol: string;
          readonly quantity: number;
          readonly rationale: string;
          readonly sourceRefs: readonly string[];
          readonly answerId?: string;
        },
  ): Promise<TradeProposal> {
    return this.command<TradeProposal>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/trade-proposals`,
      input,
    );
  }

  /** V-A4: the goal's limits applied to one order before it is saved. Nothing is placed. */
  public previewTradeProposal(
    goalId: string,
    order:
      | { readonly side: "buy"; readonly symbol: string; readonly notional: number }
      | { readonly side: "sell"; readonly symbol: string; readonly quantity: number },
  ): Promise<TradePreview> {
    return this.command<TradePreview>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/trade-proposals/preview`,
      order,
    );
  }

  public reviewTradeProposal(
    proposalId: string,
    input:
      | { readonly decision: "approve"; readonly expectedRevision: number }
      | {
          readonly decision: "reject";
          readonly expectedRevision: number;
          readonly reason: string;
        },
  ): Promise<TradeProposal> {
    return this.command<TradeProposal>(
      `/v1/trade-proposals/${encodeURIComponent(proposalId)}/review`,
      input,
    );
  }

  public submitTradeProposal(proposalId: string, expectedRevision: number): Promise<TradeProposal> {
    return this.command<TradeProposal>(
      `/v1/trade-proposals/${encodeURIComponent(proposalId)}/submit`,
      { expectedRevision },
    );
  }

  public setGoalAutonomy(
    goalId: string,
    input: {
      readonly mode: "manual" | "autonomous";
      readonly expectedRevision: number;
      readonly screenSize?: UniverseSize;
      /** How often the goal checks (ADR-084); absent keeps the goal's bundle or the deployment's. */
      readonly cadence?: TickCadence;
    },
  ): Promise<GoalTradingProjection> {
    return this.command<GoalTradingProjection>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/autonomy`,
      input,
    );
  }

  public updateGoalTerms(
    goalId: string,
    input: {
      readonly expectedRevision: number;
      readonly targetAmount?: number;
      readonly targetDate?: string;
      readonly maxDrawdownPercent?: number;
    },
  ): Promise<GoalTradingProjection> {
    return this.mutation<GoalTradingProjection>(
      "PATCH",
      `/v1/investment-goals/${encodeURIComponent(goalId)}`,
      input,
    );
  }

  public stopGoal(goalId: string, expectedRevision: number): Promise<GoalTradingProjection> {
    return this.command<GoalTradingProjection>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/stop`,
      { expectedRevision },
    );
  }

  public archiveGoal(goalId: string, expectedRevision: number): Promise<GoalTradingProjection> {
    return this.command<GoalTradingProjection>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/archive`,
      { expectedRevision },
    );
  }

  public listGoalEvents(goalId: string, limit = 25): Promise<readonly InvestmentGoalEvent[]> {
    return this.get<readonly InvestmentGoalEvent[]>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/history?limit=${limit}`,
    );
  }

  /** Replaces the goal's trading policy; refused while autonomy is on (ADR-083). */
  public updateGoalPolicy(
    goalId: string,
    expectedRevision: number,
    policy: GoalTradingPolicy,
  ): Promise<GoalTradingProjection> {
    return this.mutation<GoalTradingProjection>(
      "PUT",
      `/v1/investment-goals/${encodeURIComponent(goalId)}/policy`,
      { expectedRevision, policy },
    );
  }

  public async listPaperAccounts(): Promise<{
    readonly accounts: readonly PaperAccount[];
    readonly acceptsNewAccounts: boolean;
    readonly acceptsAlpacaOAuth: boolean;
  }> {
    const envelope = await this.request<{
      data: readonly PaperAccount[];
      meta: { acceptsNewAccounts: boolean; acceptsAlpacaOAuth?: boolean };
    }>("/v1/paper-accounts", { method: "GET" }, false);
    return {
      accounts: envelope.data,
      acceptsNewAccounts: envelope.meta.acceptsNewAccounts,
      acceptsAlpacaOAuth: envelope.meta.acceptsAlpacaOAuth === true,
    };
  }

  /**
   * ADR-100. Starts connecting an account through Alpaca and returns where to send the admin. The
   * state inside the address is the only copy; the server keeps its hash.
   */
  public startPaperAccountOAuth(
    request:
      | { readonly purpose: "add"; readonly name: string }
      | {
          readonly purpose: "reconnect";
          readonly paperAccountId: string;
          readonly expectedRevision: number;
        },
  ): Promise<{ readonly authorizeUrl: string; readonly expiresAt: string }> {
    return this.command<{ readonly authorizeUrl: string; readonly expiresAt: string }>(
      "/v1/paper-accounts/oauth/start",
      request,
    );
  }

  /** ADR-101. The token goes to the server once; nothing in the browser keeps it afterwards. */
  public addTradierSandboxAccount(name: string, sandboxToken: string): Promise<PaperAccount> {
    return this.command<PaperAccount>("/v1/paper-accounts/tradier", { name, sandboxToken });
  }

  /** ADR-101. A new token for the same Tradier sandbox account. */
  public replaceTradierSandboxToken(
    id: string,
    expectedRevision: number,
    sandboxToken: string,
  ): Promise<PaperAccount> {
    return this.command<PaperAccount>(
      `/v1/paper-accounts/${encodeURIComponent(id)}/tradier-token`,
      { expectedRevision, sandboxToken },
    );
  }

  /** ADR-100. Posts what Alpaca sent back; the server exchanges it and stores the account. */
  public completePaperAccountOAuth(code: string, state: string): Promise<PaperAccount> {
    return this.command<PaperAccount>("/v1/paper-accounts/oauth/complete", { code, state });
  }

  /** The keys go to the server once; nothing in the browser keeps them after this call. */
  public addPaperAccount(name: string, keys: PaperAccountKeys): Promise<PaperAccount> {
    return this.command<PaperAccount>("/v1/paper-accounts", { name, ...keys });
  }

  public rotatePaperAccount(
    id: string,
    expectedRevision: number,
    keys: PaperAccountKeys,
  ): Promise<PaperAccount> {
    return this.command<PaperAccount>(`/v1/paper-accounts/${encodeURIComponent(id)}/rotate`, {
      expectedRevision,
      ...keys,
    });
  }

  /** The account's balance right now, as its broker reports it. Nothing is stored. */
  public async getPaperAccountBalance(
    id: string,
  ): Promise<{ readonly equity: number; readonly cash: number; readonly readAt: string }> {
    const envelope = await this.request<{
      data: { equity: number; cash: number; readAt: string };
    }>(`/v1/paper-accounts/${encodeURIComponent(id)}/balance`, { method: "GET" }, false);
    return envelope.data;
  }

  public retirePaperAccount(id: string, expectedRevision: number): Promise<PaperAccount> {
    return this.command<PaperAccount>(`/v1/paper-accounts/${encodeURIComponent(id)}/retire`, {
      expectedRevision,
    });
  }

  public listTickPage(goalId: string, filter: TickListFilter = {}): Promise<Page<AutonomousTick>> {
    return this.getPage<AutonomousTick>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/ticks${queryString(filter)}`,
    );
  }

  public listProposalPage(
    goalId: string,
    filter: ProposalListFilter = {},
  ): Promise<Page<TradeProposal>> {
    return this.getPage<TradeProposal>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/trade-proposals${queryString(filter)}`,
    );
  }

  public listTradeOutcomePage(
    goalId: string,
    filter: { readonly limit?: number; readonly before?: string } = {},
  ): Promise<Page<TradeOutcome>> {
    return this.getPage<TradeOutcome>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/trade-outcomes${queryString(filter)}`,
    );
  }

  public listAutonomousTicks(goalId: string, limit = 25): Promise<readonly AutonomousTick[]> {
    return this.get<readonly AutonomousTick[]>(
      `/v1/investment-goals/${encodeURIComponent(goalId)}/ticks?limit=${limit}`,
    );
  }

  public listDataCatalog(search = ""): Promise<DataCatalogPage> {
    return this.get<DataCatalogPage>(
      `/v1/data/catalog?limit=100&search=${encodeURIComponent(search)}`,
    );
  }

  public listOpenbbCapabilityPacks(): Promise<OpenbbCapabilityPackCatalog> {
    return this.get<OpenbbCapabilityPackCatalog>("/v1/data/capability-packs");
  }

  public getDataCommand(commandId: string, version?: string): Promise<DataCommandManifest> {
    const query = version === undefined ? "" : `?version=${encodeURIComponent(version)}`;
    return this.get<DataCommandManifest>(
      `/v1/data/commands/${encodeURIComponent(commandId)}${query}`,
    );
  }

  public createDataQueryRun(input: {
    readonly commandId: string;
    readonly commandVersion?: string;
    readonly providerKey: string;
    readonly connectionId?: string;
    readonly parameters: Readonly<Record<string, unknown>>;
  }): Promise<DataQueryRun> {
    return this.command<DataQueryRun>("/v1/data/query-runs", {
      ...input,
      purpose: "interactive_research",
    });
  }

  public listDataQueryRuns(): Promise<readonly DataQueryRun[]> {
    return this.get<readonly DataQueryRun[]>("/v1/data/query-runs?limit=100");
  }

  public getDataQueryRun(runId: string): Promise<DataQueryRun> {
    return this.get<DataQueryRun>(`/v1/data/query-runs/${encodeURIComponent(runId)}`);
  }

  public getDatasetPreview(datasetId: string, limit = 100): Promise<DatasetPreview> {
    return this.get<DatasetPreview>(
      `/v1/datasets/${encodeURIComponent(datasetId)}/preview?limit=${limit}`,
    );
  }

  public getDatasetProvenance(datasetId: string): Promise<Record<string, unknown>> {
    return this.get<Record<string, unknown>>(
      `/v1/datasets/${encodeURIComponent(datasetId)}/provenance`,
    );
  }

  public listAnalysisMethods(): Promise<readonly AnalysisMethod[]> {
    return this.get<readonly AnalysisMethod[]>("/v1/analysis-methods");
  }

  public listAnalysisWorkspaces(): Promise<readonly AnalysisWorkspace[]> {
    return this.get<readonly AnalysisWorkspace[]>("/v1/analysis-workspaces");
  }

  public createAnalysisWorkspace(input: {
    readonly name: string;
    readonly description?: string;
  }): Promise<AnalysisWorkspace> {
    return this.command<AnalysisWorkspace>("/v1/analysis-workspaces", input);
  }

  public listAnalysisRuns(): Promise<readonly AnalysisRun[]> {
    return this.get<readonly AnalysisRun[]>("/v1/analysis-runs?limit=100");
  }

  public createAnalysisRun(input: {
    readonly workspaceId: string;
    readonly methodId: string;
    readonly methodVersion: string;
    readonly datasetIds: readonly string[];
    readonly parameters: Readonly<Record<string, unknown>>;
  }): Promise<AnalysisRun> {
    return this.command<AnalysisRun>("/v1/analysis-runs", { ...input, purpose: "analysis" });
  }

  public getAnalysisRun(runId: string): Promise<AnalysisRun> {
    return this.get<AnalysisRun>(`/v1/analysis-runs/${encodeURIComponent(runId)}`);
  }

  public getAnalysisArtifact(artifactId: string): Promise<AnalysisArtifact> {
    return this.get<AnalysisArtifact>(`/v1/analysis-artifacts/${encodeURIComponent(artifactId)}`);
  }

  public searchDocuments(
    query: string,
    entityKey?: string,
    limit = 20,
  ): Promise<GovernedDocumentSearchResult> {
    const parameters = new URLSearchParams({ q: query, limit: String(limit) });
    if (entityKey !== undefined && entityKey.trim().length > 0)
      parameters.set("entityKey", entityKey.trim());
    return this.get<GovernedDocumentSearchResult>(`/v1/documents/search?${parameters.toString()}`);
  }

  public listDocumentEventCandidates(
    status: DocumentEventCandidate["status"] = "pending_review",
  ): Promise<readonly DocumentEventCandidate[]> {
    return this.get<readonly DocumentEventCandidate[]>(
      `/v1/document-event-candidates?status=${encodeURIComponent(status)}&limit=100`,
    );
  }

  public reviewDocumentEventCandidate(
    candidateId: string,
    decision: "accepted" | "rejected",
  ): Promise<DocumentEventCandidate> {
    return this.command<DocumentEventCandidate>(
      `/v1/document-event-candidates/${encodeURIComponent(candidateId)}/review`,
      { decision },
    );
  }

  public listMonitorSourceBindings(bookId?: string): Promise<readonly MonitorSourceBinding[]> {
    return this.get<readonly MonitorSourceBinding[]>(
      `/v1/monitor-source-bindings${bookId === undefined ? "" : `?bookId=${encodeURIComponent(bookId)}`}`,
    );
  }

  public upsertMonitorSourceBinding(
    contractId: string,
    input: {
      readonly commandId: string;
      readonly commandVersion?: string;
      readonly providerKey: string;
      readonly connectionId?: string;
      readonly parameters: Readonly<Record<string, unknown>>;
      readonly selector: {
        readonly rowSelection: "first" | "latest_observed_at" | "latest_published_at";
        readonly valuePath: string;
        readonly observedAtPath?: string;
        readonly publishedAtPath?: string;
        readonly sourceRecordIdPath?: string;
        readonly correctionOfPath?: string;
        readonly valueScale?: number;
      };
      readonly metricKey: string;
      readonly unit: string;
      readonly pollCadenceMinutes: number;
      readonly freshnessSlaMinutes: number;
      readonly correctionBehavior: "append_revision" | "replace_snapshot" | "provider_defined";
      readonly materiality: {
        readonly minimumAbsoluteChange: number;
        readonly minimumRelativeChangePct: number;
      };
      readonly enabled: boolean;
    },
  ): Promise<MonitorSourceBinding> {
    return this.command<MonitorSourceBinding>(
      `/v1/evidence-contracts/${encodeURIComponent(contractId)}/monitor-source`,
      input,
    );
  }

  public listDataProviders(): Promise<readonly DataProviderCatalogRecord[]> {
    return this.get<readonly DataProviderCatalogRecord[]>("/v1/data/providers");
  }

  public listDataProviderConnections(): Promise<readonly DataProviderConnectionRecord[]> {
    return this.get<readonly DataProviderConnectionRecord[]>("/v1/data/provider-connections");
  }

  public listAvailableDataProviderConnections(): Promise<readonly DataProviderConnectionOption[]> {
    return this.get<readonly DataProviderConnectionOption[]>(
      "/v1/data/provider-connections/available",
    );
  }

  public createDataProviderConnection(input: {
    readonly name: string;
    readonly providerKey: string;
    readonly entitlementMode: "customer_supplied" | "customer_entitled";
    readonly credentialRef?: string;
    readonly rightsProfileId: string;
    readonly providerQuota: DataProviderBudget;
    readonly config: DataProviderConnectionConfig;
    readonly status: "draft" | "enabled";
  }): Promise<DataProviderConnectionRecord> {
    return this.command<DataProviderConnectionRecord>("/v1/data/provider-connections", input);
  }

  public updateDataProviderConnection(
    connectionId: string,
    revision: number,
    input: {
      readonly name?: string;
      readonly credentialRef?: string;
      readonly rightsProfileId?: string;
      readonly providerQuota?: DataProviderBudget;
      readonly config?: DataProviderConnectionConfig;
      readonly status?: "enabled" | "suspended";
    },
  ): Promise<DataProviderConnectionRecord> {
    return this.mutation("PATCH", `/v1/data/provider-connections/${connectionId}`, input, {
      "If-Match": `"${revision}"`,
    });
  }

  public testDataProviderConnection(connectionId: string): Promise<DataQueryRun> {
    return this.command<DataQueryRun>(`/v1/data/provider-connections/${connectionId}/test`, {});
  }

  /** Best-effort wakeup caused only by a real user opening Ask Polyfuture. */
  public async prewarmAgent(): Promise<void> {
    try {
      await this.fetcher("/v1/agent/prewarm", {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
      });
    } catch {
      // Navigation must remain usable when a speculative wakeup loses the network race.
    }
  }

  public sendAgentMessage(input: {
    chatId: string;
    turns: readonly AgentConversationTurn[];
    selectedBookId?: string;
  }): Promise<AgentConversationReply> {
    return this.command<AgentConversationReply>("/v1/agent/conversation", {
      schemaVersion: "1.0.0",
      chatId: input.chatId,
      turns: input.turns,
      ...(input.selectedBookId === undefined ? {} : { selectedBookId: input.selectedBookId }),
    });
  }

  public async streamAgentMessage(
    input: {
      chatId: string;
      turns: readonly AgentConversationTurn[];
      selectedBookId?: string;
      /** ADR-092. Ready files attached to the latest question. */
      attachmentIds?: readonly string[];
    },
    handlers: {
      readonly onDelta: (delta: string) => void;
      readonly onStatus?: (status: AgentStreamStatus) => void;
      readonly onTiming?: (timing: AgentStreamTiming) => void;
    },
    signal?: AbortSignal,
  ): Promise<AgentConversationReply> {
    const csrfToken = findCsrfToken(this.cookieReader());
    if (csrfToken === undefined && this.csrfTokenRequired) {
      throw new ApiRequestError("Your session is missing its request token. Sign in again.", 409);
    }
    const response = await this.fetcher("/v1/agent/conversation/stream", {
      method: "POST",
      ...(signal === undefined ? {} : { signal }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
        ...(csrfToken === undefined ? {} : { "X-CSRF-Token": csrfToken }),
      },
      body: JSON.stringify({
        schemaVersion: "1.0.0",
        chatId: input.chatId,
        turns: input.turns,
        ...(input.selectedBookId === undefined ? {} : { selectedBookId: input.selectedBookId }),
        ...(input.attachmentIds === undefined || input.attachmentIds.length === 0
          ? {}
          : { attachmentIds: input.attachmentIds }),
      }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        for (const listener of this.unauthorizedListeners) listener();
      }
      const body = await safeJson<ApiErrorEnvelope>(response);
      throw new ApiRequestError(
        replaceProductVocabulary(
          body?.error?.message ?? `Request failed with status ${response.status}`,
        ),
        response.status,
        body?.error?.code,
        body?.error?.trace_id,
      );
    }
    if (response.body === null) {
      throw new ApiRequestError("The agent stream did not open.", 502, "agent_stream_missing");
    }
    let completed: AgentConversationReply | undefined;
    await readSseResponse(response.body, (event, data) => {
      if (event === "reply.delta") {
        const parsed = data as { delta?: unknown };
        if (typeof parsed.delta === "string") handlers.onDelta(parsed.delta);
        return;
      }
      if (event === "reply.status") {
        const parsed = data as {
          phase?: unknown;
          message?: unknown;
          toolName?: unknown;
          step?: { stepId?: unknown; label?: unknown; status?: unknown };
        };
        if (typeof parsed.phase === "string" && typeof parsed.message === "string") {
          const step = parsed.step;
          const status = step?.status;
          handlers.onStatus?.({
            phase: parsed.phase,
            message: parsed.message,
            ...(typeof parsed.toolName === "string" ? { toolName: parsed.toolName } : {}),
            ...(typeof step?.stepId === "string" &&
            typeof step.label === "string" &&
            (status === "running" || status === "done" || status === "failed")
              ? { step: { stepId: step.stepId, label: step.label, status } }
              : {}),
          });
        }
        return;
      }
      if (event === "reply.timing") {
        const parsed = data as { stage?: unknown; elapsedMs?: unknown; transport?: unknown };
        if (
          typeof parsed.stage === "string" &&
          /^[a-z][a-z0-9_]{0,63}$/.test(parsed.stage) &&
          typeof parsed.elapsedMs === "number" &&
          Number.isFinite(parsed.elapsedMs) &&
          typeof parsed.transport === "string"
        ) {
          const timing = {
            stage: parsed.stage,
            elapsedMs: Math.max(0, parsed.elapsedMs),
            transport: parsed.transport,
          };
          globalThis.performance?.mark?.(`polyfuture.agent.${timing.stage}`, {
            detail: { elapsedMs: timing.elapsedMs, transport: timing.transport },
          });
          handlers.onTiming?.(timing);
        }
        return;
      }
      if (event === "reply.completed") {
        completed = data as AgentConversationReply;
        return;
      }
      if (event === "reply.error") {
        const error = data as { code?: unknown; message?: unknown; trace_id?: unknown };
        throw new ApiRequestError(
          typeof error.message === "string" ? error.message : "The agent could not answer.",
          502,
          typeof error.code === "string" ? error.code : "agent_runtime_failed",
          typeof error.trace_id === "string" ? error.trace_id : undefined,
        );
      }
    });
    if (completed === undefined) {
      throw new ApiRequestError(
        "The agent stream ended before the answer was complete.",
        502,
        "agent_stream_incomplete",
      );
    }
    return completed;
  }

  public getTodayProjection(): Promise<TodayProjection> {
    return this.get<TodayProjection>("/v1/ui/today");
  }

  /** Records a Dashboard visit and returns the one before it (V-D1). */
  public recordDashboardVisit(): Promise<DashboardVisit> {
    return this.command<DashboardVisit>("/v1/ui/dashboard/visit", {});
  }

  public getDashboard(options: {
    readonly since: string;
    readonly bookId?: string;
  }): Promise<DashboardProjection> {
    return this.get<DashboardProjection>(`/v1/ui/dashboard${queryString(options)}`);
  }

  public getValuationSummary(bookId: string): Promise<ValuationSummary> {
    return this.get<ValuationSummary>(`/v1/books/${encodeURIComponent(bookId)}/valuation-summary`);
  }

  /** Publishes one governed holding into a portfolio. Values are profile-contract keyed. */
  public createPosition(
    bookId: string,
    input: { values: Record<string, unknown>; note?: string; tags?: string[] },
  ): Promise<PositionRecord> {
    return this.command<PositionRecord>(`/v1/books/${bookId}/positions`, {
      values: input.values,
      note: input.note ?? "",
      tags: input.tags ?? [],
      identifiers: [],
    });
  }

  /** Publishes a reviewed holding set atomically as one governed book version. */
  public createPositions(
    bookId: string,
    positions: { values: Record<string, unknown>; note?: string; tags?: string[] }[],
  ): Promise<{
    records: PositionRecord[];
    bookVersionId: string;
    versionNo: number;
    initialCoverage?: {
      bookId: string;
      started: boolean;
      reason: "coverage_started" | "auto_coverage_disabled" | "scenarios_already_exist";
      coverageRun?: ScenarioRunRecord;
    };
  }> {
    return this.command(`/v1/books/${bookId}/positions/batch`, {
      positions: positions.map((input) => ({
        values: input.values,
        note: input.note ?? "",
        tags: input.tags ?? [],
        identifiers: [],
      })),
    });
  }

  /** Revises a governed assumption and publishes a new immutable portfolio version. */
  public updateAssumption(
    assumptionId: string,
    input: {
      expectedRevision: number;
      text?: string;
      confidence?: AssumptionRecord["confidence"];
      confidenceRationale?: string | null;
      falsificationText?: string | null;
      reviewBy?: string | null;
    },
  ): Promise<{
    record: AssumptionRecord;
    bookVersionId: string;
    versionNo: number;
    normalizedHash: string;
  }> {
    return this.command(`/v1/assumptions/${assumptionId}/update`, input);
  }

  /** Retires a governed assumption with an explicit reason and publishes a new version. */
  public retireAssumption(
    assumptionId: string,
    input: { expectedRevision: number; rationale: string },
  ): Promise<{
    record: AssumptionRecord;
    bookVersionId: string;
    versionNo: number;
    normalizedHash: string;
  }> {
    return this.command(`/v1/assumptions/${assumptionId}/retire`, input);
  }

  public listReferenceProfiles(): Promise<ReferenceProfileSummary[]> {
    return this.get<ReferenceProfileSummary[]>("/v1/reference-profiles");
  }

  /** Profile choices for signup, readable before any session exists. */
  public listOnboardingProfiles(): Promise<ReferenceProfileSummary[]> {
    return this.get<ReferenceProfileSummary[]>("/v1/onboarding/reference-profiles");
  }

  public getAuthConfig(): Promise<AuthClientConfig> {
    return this.get<AuthClientConfig>("/v1/auth/config");
  }

  /** Exchanges a verified Identity Platform token for the opaque browser session. */
  public exchangeSession(idToken: string, orgId?: string): Promise<unknown> {
    return this.request<unknown>("/v1/auth/session", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ idToken, ...(orgId === undefined ? {} : { orgId }) }),
    });
  }

  /**
   * Creates the organization and its first admin membership. This runs before any session exists,
   * so it carries the identity token directly rather than a session cookie.
   */
  public createOrganization(input: CreateOrganizationInput): Promise<CreatedOrganization> {
    return this.request<CreatedOrganization>("/v1/orgs", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  public createBook(input: CreateBookInput): Promise<BookRecord> {
    return this.command<BookRecord>("/v1/books", input);
  }

  public listResearchCases(): Promise<ResearchCaseSummary[]> {
    return this.get<ResearchCaseSummary[]>("/v1/research-cases");
  }

  public getResearchCase(): Promise<ResearchCaseManifest> {
    return this.get<ResearchCaseManifest>("/v1/research-cases/situational-awareness-second-order");
  }

  public replayResearchCase(input: CaseReplayInput): Promise<CaseReplayResult> {
    return this.request<CaseReplayResult>(
      "/v1/research-cases/situational-awareness-second-order/replay",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  public async loadWorkspace(bookId: string): Promise<WorkspaceData> {
    const [book, positions, assumptions, scenarios, runs, whatIfs, valuation] = await Promise.all([
      this.get<BookRecord>(`/v1/books/${bookId}`),
      this.get<PositionRecord[]>(`/v1/books/${bookId}/positions`),
      this.get<AssumptionRecord[]>(`/v1/books/${bookId}/assumptions`),
      this.get<ScenarioRecord[]>(`/v1/books/${bookId}/scenarios`),
      this.get<ScenarioRunRecord[]>(`/v1/books/${bookId}/scenario-runs`),
      this.get<WhatIfRecord[]>(`/v1/books/${bookId}/whatifs`),
      // The price freshness and move the Dashboard also shows; the page still loads without it.
      this.getValuationSummary(bookId).catch(() => undefined),
    ]);
    return {
      book,
      positions,
      assumptions,
      scenarios,
      runs,
      whatIfs,
      ...(valuation === undefined ? {} : { valuation }),
    };
  }

  public async loadImportReview(bookId: string): Promise<ImportReviewData> {
    const [book, mappings, rightsProfiles, imports] = await Promise.all([
      this.get<BookRecord>(`/v1/books/${bookId}`),
      this.get<ImportMappingRecord[]>("/v1/import-mappings"),
      this.get<DataRightsProfileRecord[]>("/v1/data-rights-profiles"),
      this.get<BookImportRecord[]>(`/v1/books/${bookId}/imports`),
    ]);
    return {
      book,
      mappings: mappings.filter(
        (mapping) =>
          mapping.status === "published" &&
          mapping.mapping.profileKey === book.profileKey &&
          mapping.mapping.profileVersion === book.profileVersion,
      ),
      rightsProfiles,
      imports,
    };
  }

  public getBookImport(importId: string): Promise<BookImportRecord> {
    return this.get<BookImportRecord>(`/v1/book-imports/${importId}`);
  }

  public async loadAdministration(): Promise<AdministrationData> {
    const [
      organization,
      users,
      connections,
      rightsProfiles,
      mappings,
      webhookEndpoints,
      artifactDeliveries,
      auditEvents,
    ] = await Promise.all([
      this.get<OrganizationRecord>("/v1/org"),
      this.get<OrganizationUserRecord[]>("/v1/users"),
      this.get<IntegrationConnectionRecord[]>("/v1/integration-connections"),
      this.get<DataRightsProfileRecord[]>("/v1/data-rights-profiles"),
      this.get<ImportMappingRecord[]>("/v1/import-mappings"),
      this.get<OutboundWebhookEndpointRecord[]>("/v1/outbound-webhook-endpoints"),
      this.get<ArtifactDeliveryRecord[]>("/v1/artifact-deliveries?limit=100"),
      this.get<AdministrationData["auditEvents"]>("/v1/org/audit-events"),
    ]);
    return {
      organization,
      users,
      connections,
      rightsProfiles,
      mappings: mappings.filter((mapping) => mapping.status === "published"),
      webhookEndpoints,
      artifactDeliveries,
      auditEvents,
    };
  }

  public getOrganization(): Promise<OrganizationRecord> {
    return this.get<OrganizationRecord>("/v1/org");
  }

  public listExternalMcpClients(): Promise<ExternalMcpClientRecord[]> {
    return this.get<ExternalMcpClientRecord[]>("/v1/admin/external-mcp-clients");
  }

  public createExternalMcpClient(input: {
    name: string;
    redirectUris: string[];
    allowedScopes: ExternalMcpScope[];
  }): Promise<ExternalMcpClientRecord> {
    return this.command<ExternalMcpClientRecord>("/v1/admin/external-mcp-clients", input);
  }

  public revokeExternalMcpClient(clientId: string): Promise<void> {
    return this.mutation("DELETE", `/v1/admin/external-mcp-clients/${clientId}`, undefined);
  }

  public listCapabilityRolloutFlags(): Promise<CapabilityRolloutFlagRecord[]> {
    return this.get<CapabilityRolloutFlagRecord[]>("/v1/admin/capability-rollout");
  }

  public setCapabilityRolloutFlag(input: {
    dimension: CapabilityRolloutDimension;
    key: string;
    enabled: boolean;
    reason: string;
    expectedRevision: number;
  }): Promise<CapabilityRolloutFlagRecord> {
    return this.command<CapabilityRolloutFlagRecord>("/v1/admin/capability-rollout", input);
  }

  public createOutboundWebhookEndpoint(input: {
    name: string;
    url: string;
    rightsProfileId: string;
    enabled: boolean;
  }): Promise<OutboundWebhookEndpointRecord> {
    return this.command("/v1/outbound-webhook-endpoints", input);
  }

  public setOutboundWebhookEndpointStatus(
    endpointId: string,
    enabled: boolean,
    expectedRevision: number,
  ): Promise<OutboundWebhookEndpointRecord> {
    return this.command(`/v1/outbound-webhook-endpoints/${endpointId}/status`, {
      enabled,
      expectedRevision,
    });
  }

  public updateOrganization(
    name: string,
    settings: Record<string, unknown>,
  ): Promise<OrganizationRecord> {
    return this.mutation("PATCH", "/v1/org", { name, settings });
  }

  public createOrganizationUser(input: {
    email: string;
    identitySubject: string;
    identityTenantId?: string;
    role: "viewer" | "analyst" | "admin";
  }): Promise<OrganizationUserRecord> {
    return this.command("/v1/users", input);
  }

  public updateOrganizationUser(
    userId: string,
    input: {
      role: "viewer" | "analyst" | "admin";
      active: boolean;
      expectedMembershipRevision: number;
    },
  ): Promise<OrganizationUserRecord> {
    return this.mutation("PATCH", `/v1/users/${userId}`, input);
  }

  public deactivateOrganizationUser(userId: string, membershipRevision: number): Promise<void> {
    return this.mutation("DELETE", `/v1/users/${userId}`, undefined, {
      "If-Match": String(membershipRevision),
    });
  }

  public createIntegrationConnection(input: {
    name: string;
    entitlementMode: "customer_supplied" | "customer_entitled";
    credentialRef?: string;
    rightsProfileId: string;
    config: SftpConnectionConfig;
    status: "draft" | "enabled";
  }): Promise<IntegrationConnectionRecord> {
    return this.command("/v1/integration-connections", input);
  }

  public updateIntegrationConnection(
    connectionId: string,
    revision: number,
    input: {
      name?: string;
      credentialRef?: string;
      rightsProfileId?: string;
      config?: SftpConnectionConfig;
      status?: "enabled" | "suspended";
    },
  ): Promise<IntegrationConnectionRecord> {
    return this.mutation("PATCH", `/v1/integration-connections/${connectionId}`, input, {
      "If-Match": `"${revision}"`,
    });
  }

  public retireIntegrationConnection(connectionId: string, revision: number): Promise<void> {
    return this.mutation("DELETE", `/v1/integration-connections/${connectionId}`, undefined, {
      "If-Match": `"${revision}"`,
    });
  }

  public queueIntegrationRun(
    connectionId: string,
    runType: "test" | "sync",
  ): Promise<IntegrationRunRecord> {
    return this.command(`/v1/integration-connections/${connectionId}/${runType}`, {});
  }

  public listIntegrationRuns(connectionId: string): Promise<IntegrationRunRecord[]> {
    return this.get<IntegrationRunRecord[]>(`/v1/integration-connections/${connectionId}/runs`);
  }

  public createBookSourceBinding(
    bookId: string,
    input: {
      connectionId: string;
      mappingVersionId: string;
      authority: "source_only" | "source_plus_overrides";
      syncMode: "manual" | "scheduled";
      schedule?: string;
      materialityPolicy: {
        changedRecordCount: number;
        changedRecordFraction: number;
        absoluteCurrencyDelta?: number;
      };
    },
  ): Promise<BookSourceBindingRecord> {
    return this.command(`/v1/books/${bookId}/source-binding`, input);
  }

  public listIdentityReview(sourceSnapshotId: string): Promise<IdentityResolutionAttempt[]> {
    const query = new URLSearchParams({ source_snapshot_id: sourceSnapshotId, limit: "500" });
    return this.get<IdentityResolutionAttempt[]>(`/v1/identity-resolution-attempts?${query}`);
  }

  /**
   * ADR-092. Reserves an upload for a chat attachment, sends the bytes to quarantine (the signed
   * bucket POST, or the local development route), and says it is done. The server then processes
   * it; poll `getChatAttachment` until it is ready or refused.
   */
  public async uploadChatAttachment(file: File, mediaType: string): Promise<ChatAttachment> {
    const contentSha256 = await sha256File(file);
    const reserved = await this.command<{
      attachment: ChatAttachment;
      upload: ChatAttachmentUploadSession;
    }>("/v1/chat-attachments", {
      fileName: file.name,
      mediaType,
      sizeBytes: file.size,
      contentSha256,
    });
    const session = reserved.upload;
    let uploaded: Response;
    if (session.uploadMethod === "POST") {
      const form = new FormData();
      for (const [name, value] of Object.entries(session.formFields)) form.append(name, value);
      form.append("file", file, file.name);
      uploaded = await this.fetcher(session.uploadUrl, {
        method: "POST",
        body: form,
        credentials: "omit",
      });
    } else {
      const csrfToken = findCsrfToken(this.cookieReader());
      uploaded = await this.fetcher(session.uploadUrl, {
        method: "PUT",
        body: file,
        credentials: "same-origin",
        headers: {
          "Content-Type": mediaType,
          ...(csrfToken === undefined ? {} : { "X-CSRF-Token": csrfToken }),
        },
      });
    }
    if (!uploaded.ok) {
      throw new ApiRequestError(
        `The upload failed with status ${uploaded.status}.`,
        uploaded.status,
        "quarantine_upload_failed",
      );
    }
    return this.command<ChatAttachment>(
      `/v1/chat-attachments/${encodeURIComponent(session.attachmentId)}/complete`,
      undefined,
    );
  }

  public getChatAttachment(id: string): Promise<ChatAttachment> {
    return this.request<ChatAttachment>(`/v1/chat-attachments/${encodeURIComponent(id)}`, {
      method: "GET",
    });
  }

  public async reserveAndUploadImport(file: File): Promise<QuarantineUploadSession> {
    if (file.size === 0) throw new ApiRequestError("Choose a non-empty CSV or XLSX file.", 400);
    const mediaType = importMediaType(file.name);
    const contentSha256 = await sha256File(file);
    const session = await this.command<QuarantineUploadSession>("/v1/book-imports/upload-session", {
      fileName: file.name,
      mediaType,
      contentSha256,
    });
    if (file.size > session.constraints.maximumBytes) {
      throw new ApiRequestError(
        `The file exceeds the ${formatBytes(session.constraints.maximumBytes)} import limit.`,
        413,
      );
    }
    const form = new FormData();
    for (const [name, value] of Object.entries(session.formFields)) form.append(name, value);
    form.append("file", file, file.name);
    const uploaded = await this.fetcher(session.uploadUrl, {
      method: session.uploadMethod,
      body: form,
      credentials: "omit",
    });
    if (!uploaded.ok) {
      throw new ApiRequestError(
        `The quarantine upload failed with status ${uploaded.status}.`,
        uploaded.status,
        "quarantine_upload_failed",
      );
    }
    return session;
  }

  public stageBookImport(
    bookId: string,
    input: {
      uploadId: string;
      mappingVersionId: string;
      rightsProfileId: string;
      sourceAsOf: string;
    },
  ): Promise<BookImportRecord> {
    return this.command<BookImportRecord>(`/v1/books/${bookId}/imports`, input);
  }

  public acceptBookImport(
    importId: string,
    previewDigest: string,
    acknowledgedWarnings: string[],
  ): Promise<{ bookId: string; bookVersionId: string; versionNo: number }> {
    return this.command(
      `/v1/book-imports/${importId}/accept`,
      { acknowledgedWarnings },
      { "If-Match": `"${previewDigest}"` },
    );
  }

  public rejectBookImport(importId: string, previewDigest: string): Promise<BookImportRecord> {
    return this.command(
      `/v1/book-imports/${importId}/reject`,
      {},
      { "If-Match": `"${previewDigest}"` },
    );
  }

  public correctIdentityResolution(
    attemptId: string,
    selectedCanonicalKey: string,
    rationale: string,
  ): Promise<IdentityResolutionAttempt> {
    return this.command(`/v1/identity-resolution-attempts/${attemptId}/corrections`, {
      selectedCanonicalKey,
      rationale,
    });
  }

  public createScenarioRun(bookId: string): Promise<ScenarioRunRecord> {
    return this.command<ScenarioRunRecord>(`/v1/books/${bookId}/scenario-runs`, {
      kind: "targeted",
    });
  }

  public getScenarioRun(runId: string): Promise<ScenarioRunRecord> {
    return this.get<ScenarioRunRecord>(`/v1/scenario-runs/${runId}`);
  }

  public createScenario(bookId: string, input: ScenarioDraftInput): Promise<ScenarioRecord> {
    return this.command<ScenarioRecord>(`/v1/books/${bookId}/scenarios`, input);
  }

  public listBookScenarios(bookId: string): Promise<ScenarioRecord[]> {
    return this.get<ScenarioRecord[]>(`/v1/books/${bookId}/scenarios`);
  }

  public listScenarios(): Promise<ScenarioCatalogRecord[]> {
    return this.get<ScenarioCatalogRecord[]>("/v1/scenarios");
  }

  public getScenarioExposureMatrix(): Promise<ScenarioExposureMatrix> {
    return this.get<ScenarioExposureMatrix>("/v1/scenario-exposure-matrix");
  }

  public getScenarioBudget(): Promise<ScenarioBudgetProjection> {
    return this.get<ScenarioBudgetProjection>("/v1/scenario-budget");
  }

  public acceptScenario(scenarioId: string): Promise<ScenarioRecord> {
    return this.command<ScenarioRecord>(`/v1/scenarios/${scenarioId}/accept`, {
      rationale: "Analyst submitted the saved what-if for targeted elaboration.",
    });
  }

  public getScenario(scenarioId: string): Promise<ScenarioRecord> {
    return this.get<ScenarioRecord>(`/v1/scenarios/${scenarioId}`);
  }

  public getScenarioPath(versionId: string): Promise<MechanismProjection> {
    return this.get<MechanismProjection>(`/v1/scenario-versions/${versionId}/path`);
  }

  public getScenarioGraph(versionId: string): Promise<MechanismProjection> {
    return this.get<MechanismProjection>(`/v1/scenario-versions/${versionId}/graph?limit=200`);
  }

  public listScenarioVersions(scenarioId: string): Promise<ScenarioVersionSummary[]> {
    return this.get<ScenarioVersionSummary[]>(`/v1/scenarios/${scenarioId}/versions`);
  }

  public getScenarioVersionDiff(
    fromVersionId: string,
    toVersionId: string,
  ): Promise<MechanismVersionDiff> {
    return this.get<MechanismVersionDiff>(
      `/v1/scenario-versions/${fromVersionId}/diff/${toVersionId}`,
    );
  }

  public listEvidenceTemplates(): Promise<EvidenceTemplate[]> {
    return this.get<EvidenceTemplate[]>("/v1/evidence/templates");
  }

  public listEvidenceSources(): Promise<EvidenceSourceCatalogRecord[]> {
    return this.get<EvidenceSourceCatalogRecord[]>("/v1/evidence/sources");
  }

  public getEvidenceSourceBinding(contractId: string): Promise<EvidenceSourceBindingRecord | null> {
    return this.get<EvidenceSourceBindingRecord | null>(
      `/v1/evidence-contracts/${contractId}/source-binding`,
    );
  }

  public upsertEvidenceSourceBinding(
    contractId: string,
    input: {
      configuration: EvidenceSourceConfiguration;
      pollCadenceMinutes: number;
      enabled: boolean;
    },
  ): Promise<EvidenceSourceBindingRecord> {
    return this.command<EvidenceSourceBindingRecord>(
      `/v1/evidence-contracts/${contractId}/source-binding`,
      input,
    );
  }

  public pollEvidenceSource(contractId: string): Promise<{ scheduledFor: string }> {
    return this.command<{ scheduledFor: string }>(
      `/v1/evidence-contracts/${contractId}/source-binding:poll`,
      undefined,
    );
  }

  public listEvidenceContracts(bookId: string): Promise<EvidenceContractRecord[]> {
    return this.get<EvidenceContractRecord[]>(`/v1/books/${bookId}/evidence-contracts`);
  }

  public getEvidenceCoverage(bookId: string): Promise<EvidenceCoverage> {
    return this.get<EvidenceCoverage>(`/v1/books/${bookId}/evidence-coverage`);
  }

  public listAlertEpisodes(bookId: string): Promise<AlertEpisodeRecord[]> {
    return this.get<AlertEpisodeRecord[]>(`/v1/books/${bookId}/alert-episodes`);
  }

  public getAlertEpisode(episodeId: string): Promise<AlertEpisodeDetailRecord> {
    return this.get<AlertEpisodeDetailRecord>(`/v1/alert-episodes/${episodeId}`);
  }

  public listEvidenceObservations(contractId: string): Promise<EvidenceObservationRecord[]> {
    return this.get<EvidenceObservationRecord[]>(
      `/v1/evidence-contracts/${contractId}/observations`,
    );
  }

  public createEvidenceContract(input: {
    bookId: string;
    scenarioId?: string;
    scenarioVersionId?: string;
    tripwireId?: string;
    templateKey: string;
    templateVersion: string;
    name: string;
    ownerUserId: string;
    sourceRef: string;
    rightsRef: string;
    evidenceLinks?: { nodeKey: string; polarity: "supports" | "contradicts" }[];
    definition: EvidenceContractDefinition;
  }): Promise<EvidenceContractRecord> {
    return this.command<EvidenceContractRecord>("/v1/evidence-contracts", input);
  }

  /** ADR-091. A draft rule with no source yet; a person binds a source and approves it later. */
  public createEvidenceContractDraft(input: {
    bookId: string;
    templateKey: string;
    templateVersion: string;
    name: string;
    definition: EvidenceContractDefinition;
    proposalNote?: string;
  }): Promise<EvidenceContractRecord> {
    return this.command<EvidenceContractRecord>("/v1/evidence-contract-drafts", input);
  }

  public approveEvidenceContract(
    contractId: string,
    input: {
      sourceRef: string;
      rightsRef: string;
      definition: EvidenceContractDefinition;
    },
  ): Promise<EvidenceContractRecord> {
    return this.command<EvidenceContractRecord>(
      `/v1/evidence-contracts/${contractId}:approve`,
      input,
    );
  }

  public appendEvidenceObservation(
    contractId: string,
    input: {
      sourceRecordId: string;
      value: number;
      observedAt: string;
      publishedAt: string;
      receivedAt: string;
      vintageAt: string;
      correctionOf?: string;
      sourceRef: string;
      rightsRef: string;
      provenance: Record<string, string>;
    },
  ): Promise<unknown> {
    return this.command(`/v1/evidence-contracts/${contractId}/observations`, input);
  }

  public acknowledgeAlertEpisode(episodeId: string): Promise<AlertEpisodeRecord> {
    return this.command<AlertEpisodeRecord>(`/v1/alert-episodes/${episodeId}/acknowledge`, {});
  }

  public createThesisInput(input: {
    bookId: string;
    sourceType: "pasted_text" | "uploaded_document" | "governed_note";
    title: string;
    content: string;
    sourceRef: string;
    provenance: Record<string, string>;
  }): Promise<ThesisInputRecord> {
    return this.command<ThesisInputRecord>("/v1/thesis-inputs", input);
  }

  public stageAssumptionCandidates(
    thesisInputId: string,
    candidates: {
      text: string;
      confidence: "high" | "medium" | "low";
      confidenceRationale?: string;
      falsificationText?: string;
      reviewBy?: string;
      proposedLinks: Record<string, unknown>[];
      extractionConfidence: number;
      provenance: Record<string, string>;
    }[],
  ): Promise<AssumptionCandidateRecord[]> {
    return this.command<AssumptionCandidateRecord[]>(
      `/v1/thesis-inputs/${thesisInputId}/candidates`,
      { extractionMethod: "deterministic_review.v1", candidates },
    );
  }

  public startExtraction(thesisInputId: string): Promise<ExtractionRunRecord> {
    return this.command<ExtractionRunRecord>(
      `/v1/thesis-inputs/${thesisInputId}/extraction-runs`,
      {},
    );
  }

  public listExtractionRuns(bookId: string): Promise<ExtractionRunRecord[]> {
    return this.get<ExtractionRunRecord[]>(`/v1/books/${bookId}/extraction-runs`);
  }

  public listAssumptionCandidates(bookId: string): Promise<AssumptionCandidateRecord[]> {
    return this.get<AssumptionCandidateRecord[]>(`/v1/books/${bookId}/assumption-candidates`);
  }

  public reviewExtractionRun(
    extractionRunId: string,
    input: {
      decisions: (
        | {
            candidateId: string;
            action: "accept";
            editedText?: string;
            confidence?: "high" | "medium" | "low";
            falsificationText?: string;
            reviewBy?: string;
            rationale: string;
          }
        | { candidateId: string; action: "reject"; rationale: string }
      )[];
    },
  ): Promise<ExtractionReviewCommitRecord> {
    return this.command<ExtractionReviewCommitRecord>(
      `/v1/extraction-runs/${extractionRunId}/review`,
      input,
    );
  }

  public getAssumptionGovernance(bookId: string): Promise<AssumptionGovernanceProjection> {
    return this.get<AssumptionGovernanceProjection>(`/v1/books/${bookId}/assumption-governance`);
  }

  public acceptAssumptionCandidate(
    candidateId: string,
    input: {
      editedText?: string;
      confidence?: "high" | "medium" | "low";
      falsificationText?: string;
      reviewBy?: string;
      rationale: string;
    },
  ): Promise<AssumptionCandidateRecord> {
    return this.command<AssumptionCandidateRecord>(
      `/v1/assumption-candidates/${candidateId}/accept`,
      input,
    );
  }

  public rejectAssumptionCandidate(
    candidateId: string,
    rationale: string,
  ): Promise<AssumptionCandidateRecord> {
    return this.command<AssumptionCandidateRecord>(
      `/v1/assumption-candidates/${candidateId}/reject`,
      { rationale },
    );
  }

  public quickAddAssumption(input: {
    bookId: string;
    text: string;
    confidence: "high" | "medium" | "low";
    falsificationText?: string;
    reviewBy?: string;
  }): Promise<ExtractionRunRecord> {
    return this.command<ExtractionRunRecord>("/v1/assumptions/quick-add", input);
  }

  public reaffirmAssumption(
    assumptionId: string,
    input: { expectedRevision: number; reviewBy: string; rationale: string },
  ): Promise<{ assumptionId: string; revision: number; reviewBy: string }> {
    return this.command(`/v1/assumptions/${assumptionId}/reaffirm`, input);
  }

  public supersedeAssumption(
    assumptionId: string,
    input: {
      expectedRevision: number;
      text: string;
      confidence: "high" | "medium" | "low";
      confidenceRationale?: string;
      falsificationText?: string;
      reviewBy?: string;
      rationale: string;
    },
  ): Promise<{ assumptionId: string; supersedesId: string; bookVersionId: string }> {
    return this.command(`/v1/assumptions/${assumptionId}/supersede`, input);
  }

  public listInvestigations(bookId: string): Promise<InvestigationRecord[]> {
    return this.get<InvestigationRecord[]>(`/v1/books/${bookId}/investigations`);
  }

  public getInvestigation(id: string): Promise<InvestigationRecord> {
    return this.get<InvestigationRecord>(`/v1/investigations/${id}`);
  }

  public createInvestigation(input: {
    episodeId: string;
    scenarioId?: string;
    title: string;
    severity: "low" | "medium" | "high" | "critical";
    ownerUserId: string;
    dueAt: string;
  }): Promise<InvestigationRecord> {
    return this.command<InvestigationRecord>("/v1/investigations", input);
  }

  public addInvestigationNote(id: string, note: string): Promise<InvestigationRecord> {
    return this.command<InvestigationRecord>(`/v1/investigations/${id}/notes`, { note });
  }

  public transitionInvestigation(
    id: string,
    status: "investigating" | "escalated",
    note: string,
  ): Promise<InvestigationRecord> {
    return this.command<InvestigationRecord>(`/v1/investigations/${id}/transition`, {
      status,
      note,
    });
  }

  public resolveInvestigation(
    id: string,
    resolution: "confirmed" | "falsified" | "inconclusive" | "duplicate",
    note: string,
  ): Promise<InvestigationRecord> {
    return this.command<InvestigationRecord>(`/v1/investigations/${id}/resolve`, {
      resolution,
      note,
    });
  }

  public createResponseAction(
    investigationId: string,
    input: {
      templateKey: string;
      title: string;
      description: string;
      ownerUserId?: string;
      dueAt?: string;
      scenarioId?: string;
    },
  ): Promise<unknown> {
    return this.command(`/v1/investigations/${investigationId}/actions`, input);
  }

  public updateResponseAction(
    id: string,
    input:
      | { action: "own"; ownerUserId: string; dueAt?: string }
      | { action: "complete"; completionNote: string }
      | { action: "cancel"; note: string },
  ): Promise<unknown> {
    return this.command(`/v1/response-actions/${id}/update`, input);
  }

  public proposeReassessment(
    bookId: string,
    input: {
      investigationId?: string;
      scenarioId: string;
      triggerType:
        "schedule" | "material_book_change" | "tripwire_movement" | "confirmed_event" | "manual";
      evidenceRefs: string[];
      items: {
        targetType: "scenario" | "scenario_node" | "scenario_edge" | "evidence_link";
        targetId: string;
        field: string;
        beforeValue: unknown;
        afterValue: unknown;
        classification: "evidence_label" | "structural";
        rationale: string;
      }[];
    },
  ): Promise<ReassessmentRecord> {
    return this.command<ReassessmentRecord>(`/v1/books/${bookId}/reassessments`, input);
  }

  public decideReassessmentItem(
    id: string,
    decision: "approved" | "rejected",
    rationale: string,
  ): Promise<ReassessmentRecord> {
    return this.command<ReassessmentRecord>(`/v1/reassessment-items/${id}/decision`, {
      decision,
      rationale,
    });
  }

  public applyReassessment(id: string, note: string): Promise<ReassessmentRecord> {
    return this.command<ReassessmentRecord>(`/v1/reassessments/${id}/apply`, { note });
  }

  public listDecisionArtifacts(bookId: string): Promise<DecisionArtifactRecord[]> {
    return this.get<DecisionArtifactRecord[]>(`/v1/books/${bookId}/decision-artifacts`);
  }

  public getDecisionArtifact(id: string): Promise<DecisionArtifactRecord> {
    return this.get<DecisionArtifactRecord>(`/v1/decision-artifacts/${id}`);
  }

  public createDecisionArtifact(
    bookId: string,
    investigationId: string,
    format: "pdf" | "xlsx",
  ): Promise<DecisionArtifactRecord> {
    return this.command<DecisionArtifactRecord>(`/v1/books/${bookId}/decision-artifacts`, {
      investigationId,
      format,
      templateKey: "asset-manager-committee-v1",
    });
  }

  public transitionDecisionArtifact(
    id: string,
    action: "approve" | "publish",
  ): Promise<DecisionArtifactRecord> {
    return this.command<DecisionArtifactRecord>(`/v1/decision-artifacts/${id}/transition`, {
      action,
    });
  }

  public authorizeArtifactDownload(id: string): Promise<{ token: string; expiresAt: string }> {
    return this.command(`/v1/decision-artifacts/${id}/authorize-download`, {});
  }

  public deliverDecisionArtifact(id: string, endpointId: string): Promise<ArtifactDeliveryRecord> {
    return this.command(`/v1/decision-artifacts/${id}/deliveries`, {
      channel: "signed_webhook",
      endpointId,
    });
  }

  public listOutboundWebhookEndpoints(): Promise<OutboundWebhookEndpointRecord[]> {
    return this.get("/v1/outbound-webhook-endpoints");
  }

  public getExpansionGates(): Promise<ExpansionGateProjection> {
    return this.get<ExpansionGateProjection>("/v1/expansion-gates");
  }

  public createWhatIf(bookId: string, shock: string): Promise<WhatIfRecord> {
    return this.command<WhatIfRecord>(`/v1/books/${bookId}/whatifs`, { shock });
  }

  public promoteWhatIf(
    runId: string,
    input: { name: string; polarity: "negative" | "positive" | "mixed"; falsification: string },
  ): Promise<WhatIfPromotionRecord> {
    return this.command<WhatIfPromotionRecord>(`/v1/whatifs/${runId}/promote`, input);
  }

  public setMonitoringState(bookId: string, state: "live" | "paused") {
    return this.command<{ bookId: string; monitoringState: "live" | "paused" }>(
      `/v1/books/${bookId}/${state === "live" ? "go-live" : "pause"}`,
      {},
    );
  }

  public switchOrganization(orgId: string) {
    return this.command<unknown>("/v1/auth/session/organization", { orgId });
  }

  public async logout(): Promise<void> {
    await this.command<undefined>("/v1/auth/logout", undefined);
  }

  public streamScenarioRun(
    runId: string,
    handlers: {
      onProgress: (progress: RunProgress) => void;
      onScenarioCompleted: (scenario: ScenarioRecord) => void;
      onScenarioFailed: () => void;
      onScenarioRetry?: ((data: { scenario_id: string; attempt: number }) => void) | undefined;
      onCompleted: (run: { run_id: string; status: string; cost_usd: string }) => void;
      onConnectionOpen?: (() => void) | undefined;
      onConnectionError?: (() => void) | undefined;
    },
  ): () => void {
    const source = this.eventSourceFactory(`/v1/scenario-runs/${runId}/stream`);
    source.addEventListener("open", () => handlers.onConnectionOpen?.());
    source.addEventListener("error", () => handlers.onConnectionError?.());
    source.addEventListener("run.progress", (event) => {
      const data = parseEventData<{
        run_id: string;
        phase: RunProgress["phase"];
        done: number;
        total: number;
      }>(event);
      handlers.onProgress({
        runId: data.run_id,
        phase: data.phase,
        done: data.done,
        total: data.total,
      });
    });
    source.addEventListener("scenario.completed", (event) => {
      const data = parseEventData<{ scenario: ScenarioRecord }>(event);
      handlers.onScenarioCompleted(data.scenario);
    });
    source.addEventListener("scenario.failed", () => handlers.onScenarioFailed());
    source.addEventListener("scenario.retry", (event) => {
      handlers.onScenarioRetry?.(parseEventData<{ scenario_id: string; attempt: number }>(event));
    });
    source.addEventListener("run.completed", (event) => {
      handlers.onCompleted(
        parseEventData<{ run_id: string; status: string; cost_usd: string }>(event),
      );
      source.close();
    });
    return () => source.close();
  }

  /** A read that keeps the paging cursor the plain `get` unwraps away. */
  private getPage<T>(path: string): Promise<Page<T>> {
    return this.request<Page<T>>(path, { method: "GET" }, false);
  }

  private async get<T>(path: string): Promise<T> {
    const existing = this.pendingReads.get(path);
    if (existing !== undefined) return existing as Promise<T>;
    const pending = this.request<T>(path, { method: "GET" });
    this.pendingReads.set(path, pending);
    try {
      return await pending;
    } finally {
      if (this.pendingReads.get(path) === pending) this.pendingReads.delete(path);
    }
  }

  private async command<T>(
    path: string,
    body: unknown,
    extraHeaders: Record<string, string> = {},
  ): Promise<T> {
    return this.mutation("POST", path, body, extraHeaders);
  }

  private async mutation<T>(
    method: "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body: unknown,
    extraHeaders: Record<string, string> = {},
  ): Promise<T> {
    const csrfToken = findCsrfToken(this.cookieReader());
    if (csrfToken === undefined && this.csrfTokenRequired) {
      throw new ApiRequestError("Your session is missing its request token. Sign in again.", 409);
    }
    this.pendingReads.clear();
    try {
      return await this.request<T>(path, {
        method,
        headers: {
          // A Content-Type describes a body. Declaring one on a bodyless command makes the server
          // parse an empty payload as JSON and fail, so it is only sent when there is content.
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
          "Idempotency-Key": crypto.randomUUID(),
          ...(csrfToken === undefined ? {} : { "X-CSRF-Token": csrfToken }),
          ...extraHeaders,
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } finally {
      // A read begun before the write completed must not serve its follow-up refresh.
      this.pendingReads.clear();
    }
  }

  private reportConnection(reachable: boolean): void {
    const report = { reachable, at: Date.now() };
    for (const listener of this.connectionListeners) listener(report);
  }

  private async request<T>(path: string, init: RequestInit, unwrap = true): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const controller = new AbortController();
    const readOnly = ["GET", "HEAD", "OPTIONS"].includes(method);
    const timeout = setTimeout(() => controller.abort(), readOnly ? 30_000 : 120_000);
    try {
      const response = await this.fetcher(path, {
        ...init,
        credentials: "same-origin",
        signal: controller.signal,
      });
      this.reportConnection(true);
      if (!response.ok) {
        if (response.status === 401) {
          this.pendingReads.clear();
          for (const listener of this.unauthorizedListeners) listener();
        }
        const body = await safeJson<ApiErrorEnvelope>(response);
        throw new ApiRequestError(
          replaceProductVocabulary(
            body?.error?.message ?? `Request failed with status ${response.status}`,
          ),
          response.status,
          body?.error?.code,
          body?.error?.trace_id,
        );
      }
      if (response.status === 204) return undefined as T;
      if (!unwrap) return (await response.json()) as T;
      const envelope = (await response.json()) as ApiEnvelope<T>;
      return envelope.data;
    } catch (error) {
      if (error instanceof TypeError || (controller.signal.aborted && readOnly)) {
        this.reportConnection(false);
      }
      if (controller.signal.aborted) {
        throw new ApiRequestError(
          readOnly
            ? "This is taking too long. Check your connection and try again."
            : "The server has not confirmed this change. Refresh to check whether it saved before trying again.",
          408,
          "request_timeout",
        );
      }
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }
}

async function readSseResponse(
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: unknown) => void,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      let boundary = buffer.search(/\r?\n\r?\n/);
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        const separatorLength = buffer.slice(boundary).startsWith("\r\n\r\n") ? 4 : 2;
        buffer = buffer.slice(boundary + separatorLength);
        const event = block
          .split(/\r?\n/)
          .find((line) => line.startsWith("event:"))
          ?.slice(6)
          .trim();
        const data = block
          .split(/\r?\n/)
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n");
        if (event !== undefined && data.length > 0) onEvent(event, JSON.parse(data) as unknown);
        boundary = buffer.search(/\r?\n\r?\n/);
      }
      if (done) break;
    }
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

export async function sha256File(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

function importMediaType(
  fileName: string,
): "text/csv" | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" {
  const normalized = fileName.toLowerCase();
  if (normalized.endsWith(".csv")) return "text/csv";
  if (normalized.endsWith(".xlsx")) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  throw new ApiRequestError("Polyfuture accepts CSV and XLSX files only.", 400);
}

function formatBytes(bytes: number): string {
  return `${Math.ceil(bytes / (1024 * 1024))} MB`;
}

export function findCsrfToken(serializedCookies: string): string | undefined {
  for (const cookie of serializedCookies.split(";")) {
    const separator = cookie.indexOf("=");
    if (separator < 0) continue;
    const name = cookie.slice(0, separator).trim();
    if (name !== "polyfuture_csrf" && name !== "__Host-polyfuture_csrf") continue;
    return decodeURIComponent(cookie.slice(separator + 1).trim());
  }
  return undefined;
}

function parseEventData<T>(event: Event): T {
  if (!(event instanceof MessageEvent)) throw new TypeError("SSE event did not contain data");
  return JSON.parse(String(event.data)) as T;
}

async function safeJson<T>(response: Response): Promise<T | undefined> {
  try {
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}

function queryString(filter: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const text = params.toString();
  return text.length === 0 ? "" : `?${text}`;
}
